/**
 * The animated explainers at the head of a chapter opener
 * (components/explainer/*), and the player they run in.
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow.
 *
 * ## Why the scripts are here and not beside the essays
 *
 * An essay is prose and is translated as a file (`missions/_worlds/*.ar.md`).
 * An explainer is a drawing with a sentence under each scene, and the
 * sentences are welded to the drawing: scene four's caption is about the
 * three boxes scene four draws, and a caption list that drifted one scene out
 * of step would describe the wrong picture while reading perfectly well. So
 * they sit with the warm-up figures' labels (strings/warmup.ts, `fig`), in the
 * one place where `ar: typeof en` makes a missing or extra scene a type error
 * rather than a blank line under a moving diagram.
 *
 * Two kinds of word, as on the warm-up figures (components/warmup/figures/
 * frame.tsx): the `narration` is spoken and shown as a subtitle under the
 * stage, in the reader's direction; a `fig` value is a label drawn *on* the
 * stage. Filenames, hashes, refs and commands are neither — they stay literals
 * in the scene, because the learner types them.
 *
 * ## The narration is a list of beats
 *
 * Each scene's narration is a few sentences, and each sentence is a *beat*:
 * the moment something on the stage happens. The drawing is timed against the
 * beats rather than against the clock (`at()` in the scenes), and the voice is
 * recorded one beat at a time and measured (scripts/build-narration.mjs), so a
 * colleague appears on "a colleague joins you" in both languages even though
 * the two sentences take different times to say.
 *
 * `beats()` makes the list a tuple, so `ar: typeof en` holds the Arabic to the
 * same *number* of beats per scene as the English — one more or one fewer
 * would put every later beat on the wrong picture.
 *
 * Changing a sentence here changes what the recording must say: a manifest
 * whose text no longer matches fails web/test/explainer.test.ts, so re-run
 * `npm run build:narration` in the same change.
 */
import { ltr, num } from '../format';
import { deContinuation, jaContinuation } from './explainer-continuation';

/** A scene's narration, typed as a tuple so the two languages have the same number of beats. */
type Beats<T extends string[]> = { [K in keyof T]: string };
const beats = <T extends string[]>(...lines: T): Beats<T> => lines as Beats<T>;

export const en = {
  /* --- The player -------------------------------------------------------- */

  player: {
    /** The landmark's name, for a screen reader; nothing on the page shows it. */
    title: 'The idea, animated',
    play: 'Play',
    pause: 'Pause',
    resume: 'Resume',
    replay: 'Watch again',
    previous: 'Previous scene',
    next: 'Next scene',
    mute: 'Turn the narration off',
    unmute: 'Turn the narration on',
    volume: 'Narration volume',
    /** The speed button's own face, and what it says to a screen reader. */
    rate: (rate: number) => `${rate}×`,
    rateLabel: (rate: number) => `Playback speed ${rate}×. Change it`,
    /** A segment of the progress bar, which is a button that jumps there. */
    scene: (n: number, of: number) => `Scene ${num(n, 'en')} of ${num(of, 'en')}`,
  },

  /* --- World 02 · First Commits ------------------------------------------ */

  firstCommits: {
    narration: {
      copies: beats(
        'Every programmer’s story starts with a file that works.',
        'And with the fear of breaking it. So you make a copy. Then another.',
        'Then one called ‘actually working’.',
        'A week later, nobody remembers which one is real.',
      ),
      collision: beats(
        'Then a colleague joins you.',
        'You both open the same file on the same afternoon, and you both change it.',
        'By evening, only one version can survive.',
        'The other person’s afternoon is simply gone.',
      ),
      central: beats(
        'For decades, teams solved this with one central server that kept the whole history.',
        'Everyone else had only the latest files.',
        'It worked, until the network went down. No network, no history.',
      ),
      clones: beats(
        'In April 2005, Linus Torvalds needed a better tool for Linux, and in about two weeks, he wrote git.',
        'Its big idea: every copy is the whole thing. When you clone a project, the entire history comes with it.',
        'GitHub is just one more copy, the one everyone agreed to treat as the important one.',
      ),
      hashes: beats(
        'Every commit is a snapshot of your project. Its name is a hash, computed from its content and from the commit before it.',
        'Now change a single byte in an old commit.',
        'Its hash changes, and so does every hash after it. Nobody can rewrite the past without it showing.',
      ),
      branches: beats(
        'And a branch? It’s just a name that points at one commit.',
        'Making a new one is instant.',
        'Commit on it, and the name moves forward. Nothing else is copied.',
        'The whole branch is one small file, holding one hash.',
      ),
      threePlaces: beats(
        'Which brings us to the one idea this chapter rests on: your work can be in three places.',
        'First, the files you’re editing.',
        'Then the staging area, where you gather your next commit.',
        'Then the history, once you commit.',
        'Whenever git confuses you, ask which of the three it means. Now, go make your first commit.',
      ),
    },
    fig: {
      whichOne: 'which one is the real one?',
      you: 'you',
      colleague: 'a colleague',
      lost: 'an afternoon lost',
      server: 'the server',
      history: 'history',
      filesOnly: 'files only',
      offline: 'offline: no history',
      yourLaptop: 'your laptop',
      theirLaptop: 'a colleague’s laptop',
      agreedCopy: 'the copy everyone agreed on',
      oneByte: 'one byte changed',
      laterHashes: 'every later hash changes',
      wholeBranch: 'the whole branch:',
      workingTree: 'working tree',
      stagingArea: 'staging area',
      edited: 'edited',
      staged: 'staged',
      committed: 'committed',
    },
  },
  /* --- World 01 · The Workstation ---------------------------------------- */

  workstation: {
    narration: {
      buttons: beats(
        'Every program you have ever used has buttons.',
        'But a button can only do what somebody drew it for.',
        'Want to rename four hundred files? If nobody built that button, you click four hundred times.',
      ),
      teletype: beats(
        'The command line is older than the screen. In 1969, at Bell Labs, a terminal was a typewriter wired to a computer.',
        'Every letter was hammered onto paper, about ten a second.',
        'So commands got short names, like ls and cp.',
      ),
      pipe: beats(
        'Then, in 1973, Doug McIlroy added the pipe: one program’s output becomes the next one’s input.',
        'Search every file, keep the lines with a price, sort them, count them.',
        'Nobody built that feature. You assembled it from four small tools, in fifteen seconds.',
      ),
      survived: beats(
        'In 1984 the Macintosh arrived, and for most people the text prompt disappeared.',
        'It survived among the people who run computers for a living.',
        'Because a command is exact. It is not a description of the action; it is the action.',
      ),
      servers: beats(
        'It repeats: put it in a file, and a machine runs it ten thousand times, at four in the morning, without you.',
        'And it is where the servers are. Most of the machines that run the internet have no screen at all.',
        'The only way anyone talks to them is a line of text, like the one in front of you.',
      ),
      real: beats(
        'The terminal in this course is not a simulation: your browser is running a real Linux, on an emulated computer.',
        'Type something we never thought of, and it works, or fails with the real error.',
        'And you are graded on the state of the machine, never on what you typed.',
        'Take any route you like. Now, type your first command.',
      ),
    },
    fig: {
      open: 'Open',
      save: 'Save',
      print: 'Print',
      renameAll: 'rename 400 files',
      byHand: 'one click at a time',
      teletype: 'a teletype, 1969',
      computer: 'computer',
      tenASecond: '~10 letters a second',
      fourTools: 'four small tools, fifteen seconds',
      desktop: '1984: the desktop arrives',
      professionals: 'kept by the professionals',
      isTheAction: 'the action itself',
      everyNight: 'every night, without you',
      noScreen: 'no screen',
      realLinux: 'a real Linux, in your browser',
      keystrokes: 'what you typed',
      state: 'what the machine holds',
    },
  },
  /* --- World 03 · Branches & Remotes ------------------------------------- */

  branches: {
    narration: {
      alone: beats(
        'Until now, you worked alone. Your history was a straight line, because nothing else was happening.',
        'That changes now. From here on, the code changes while you change it.',
        'Edited by people you have never met, in timezones you have never visited.',
      ),
      peers: beats(
        'Here is what almost nobody says out loud: there is no server in git.',
        'Your repository and theirs are peers: the same kind of thing, neither one in charge.',
        'Origin is just a nickname your repository keeps for another repository’s address.',
      ),
      push: beats(
        'So sharing is something you do on purpose. You push.',
        'Your commits exist, fully and permanently, before anyone else has seen them.',
        'Which is why you can work for a week on a train with no signal, and lose nothing.',
      ),
      conflict: beats(
        'Then two people change the same lines, on the same afternoon.',
        'Git could guess which one was right. Now and then, a silent guess would delete somebody’s bug fix.',
        'So it stops, shows you both versions, and asks the only one who can decide: a person who knows what the code is for.',
      ),
      branch: beats(
        'A branch costs nothing, so every team works the same way.',
        'You make a branch for your change, work there, and propose it to the main line, where somebody reads it first.',
        'The main line stays ready to ship, and your mistakes stay on your branch.',
      ),
      routine: beats(
        'Conflicts are routine, and the way to keep them small is to have them often.',
        'The dread is universal, it is unjustified, and it disappears the third time you resolve one.',
        'Now, send your work somewhere other people can reach it.',
      ),
    },
    fig: {
      you: 'you',
      colleague: 'a colleague',
      yourRepo: 'your repository',
      theirRepo: 'theirs',
      noServer: 'no server: two equals',
      offline: 'no signal, nothing lost',
      sameLines: 'the same lines',
      yourVersion: 'yours',
      theirVersion: 'theirs',
      aPerson: 'a person decides',
      main: 'main line: always ready',
      reviewed: 'read first',
      often: 'small, often',
    },
  },
  /* --- World 04 · Recovery ----------------------------------------------- */

  recovery: {
    narration: {
      senior: beats(
        'The difference between you and a senior engineer is not talent.',
        'They delete code without ceremony, and try the restructure that probably will not work.',
        'They are not braver than you. They know that almost nothing can destroy work that was committed.',
      ),
      labels: beats(
        'A commit is an object, named by a hash of what is inside it.',
        'A branch is just a label pointing at a commit. Delete the branch, and only the label goes.',
        'The commit stays exactly where it was, fully intact. It has only become harder to name.',
      ),
      record: beats(
        'And git remembers where the labels have been.',
        'Every time a branch moves, git writes down where it was, and where it went.',
        'So the commit you lost five minutes ago is written down, by name, in a file.',
      ),
      net: beats(
        'There is one honest limit: the safety net starts at the commit.',
        'Changes you never committed are ordinary files, with no history and no net.',
        'So committing often is not tidiness. It is insurance.',
      ),
      rewrite: beats(
        'Git also lets you tidy the past: combine commits, reword them, move a whole line of work.',
        'Nobody wants to read wip, fix, actually fix, typo, six months from now.',
        'But changing a commit makes a new one. So rewrite only the history nobody else is building on.',
      ),
      disasters: beats(
        'Three small disasters are coming, and you will recover from each one.',
        'By the end, I think I lost my work should stop being something you believe.',
        'Start by finding a commit that has gone missing.',
      ),
    },
    fig: {
      deleted: 'deleted without ceremony',
      label: 'a label',
      object: 'the commit: still here',
      whereItWas: 'where it was',
      whereItWent: 'where it went',
      theRecord: 'the record of every move',
      uncommitted: 'not committed: no net',
      committed: 'committed: safe',
      yours: 'yours: tidy it',
      shared: 'shared: leave it',
      found: 'found',
    },
  },

  /* --- World 05 · Quality ------------------------------------------------ */

  quality: {
    narration: {
      question: beats(
        'Every job will ask you the same question: how do you know it works?',
        'I tested it by hand means you checked one thing, once, on one day.',
        'It says nothing about the forty other places that use this code, or about next March.',
      ),
      claim: beats(
        'A test is a small program that makes a claim about yours, and fails loudly when the claim stops being true.',
        'Add the same item to a wishlist twice. The claim: it appears once.',
        'The claim outlives you. It runs forever, and catches whoever breaks it, very often you.',
      ),
      cheap: beats(
        'It runs in a second, so it runs on every change.',
        'A check that takes a person ten minutes gets run before releases, then before big releases, then never.',
        'And a test tells you what the code is supposed to do, not just what it happens to do.',
      ),
      trace: beats(
        'When something breaks, the program leaves a stack trace.',
        'It looks like noise. It is a precise list of what the program was doing, from the innermost call to the outermost.',
        'Your own code is somewhere in the middle, and that is almost always where the answer is.',
      ),
      method: beats(
        'Then the method. Reproduce it, so you can make it happen on demand.',
        'Narrow it down with evidence, and understand why it happens, before you change anything.',
        'Fix it, then prove it: a test that fails before your fix, and passes after.',
      ),
      proof: beats(
        'A bug fixed without a test comes back, usually worse, usually to somebody else.',
        'By the end of this chapter, you will have a real answer to how you know.',
        'Start with a test suite that is already failing.',
      ),
    },
    fig: {
      byHand: 'checked by hand: once',
      callers: '40 other callers',
      claim: 'the claim',
      passes: 'passes',
      fails: 'fails loudly',
      oneSecond: '1 second, every change',
      tenMinutes: '10 minutes: then never',
      innermost: 'innermost',
      outermost: 'outermost',
      yourCode: 'your code',
      reproduce: 'reproduce',
      narrow: 'narrow',
      understand: 'understand',
      prove: 'fix, prove',
    },
  },
  /* --- World 06 · Team Git ----------------------------------------------- */

  teamGit: {
    narration: {
      reader: beats(
        'Until now, a commit was a save point, so you could get back.',
        'Here is the larger half: the history is the only lasting record of why the system is the way it is.',
        'Every commit is a message to a stranger: a colleague at three in the morning, or you, eighteen months from now.',
      ),
      message: beats(
        'A good message starts with one short line, as if finishing the sentence: this commit will.',
        'Then, if the change needs it, a few lines on why, not what.',
        'The diff already says what changed. Only you know why, and only today.',
      ),
      onecommit: beats(
        'Some tools only make sense on a team.',
        'A fix lives on one branch, and production needs exactly that fix, not the six commits around it.',
        'Git can copy the change from that one commit onto another line, as a new commit.',
      ),
      search: beats(
        'Or something worked three weeks ago and is broken now, with two hundred commits in between.',
        'Git checks out the middle one, asks whether it is good or bad, and halves the suspects each time.',
        'Two hundred commits become about eight questions.',
      ),
      coherent: beats(
        'That only works because every commit is a complete, working state of the project.',
        'Small, clean commits can be searched, reverted, copied and reviewed one at a time.',
        'One enormous commit can only be taken or left whole.',
      ),
      ticket: beats(
        'The last level is a whole ticket: branch, change, test, tidy, and share.',
        'Not the commands, the shape. It is what people actually hire for.',
        'After this chapter, your commits stop being for you. Start by moving one commit to where it is needed.',
      ),
    },
    fig: {
      why: 'why, not what',
      what: 'what: the diff has it',
      stranger: 'a stranger, at 3am',
      theFix: 'the fix',
      copied: 'copied, as a new commit',
      good: 'good',
      bad: 'bad',
      questions: 'about 8 questions',
      small: 'small and whole',
      huge: 'one enormous commit',
      branch: 'branch',
      change: 'change',
      test: 'test',
      tidy: 'tidy',
      share: 'share',
    },
  },
  /* --- World 07 · Code Review -------------------------------------------- */

  codeReview: {
    narration: {
      gate: beats(
        'At every company worth working for, there is a rule: you cannot put your own code into the main line by yourself.',
        'Someone else has to read it and agree, and that applies to everyone, even an engineer with twenty years behind them.',
        'It is not distrust. It survives everywhere because it works.',
      ),
      history: beats(
        'In 1976, Michael Fagan at IBM published inspections: read the work line by line, before it goes any further.',
        'A problem found early is cheap: ten minutes in review, or an incident, a hotfix and a customer’s trust after release.',
        'In 2008, GitHub made the same idea asynchronous: the pull request.',
      ),
      purpose: beats(
        'Catching bugs is on the list, but not at the top.',
        'Review spreads knowledge, so no one person is the only one who understands the billing code.',
        'It sets the standard, and it makes a change the team’s, not yours alone.',
      ),
      reviewed: beats(
        'To be reviewed well, keep the change small, because a reader’s attention runs out.',
        'Explain why in the description, so they review the problem and not just the code.',
        'And answer comments with curiosity. A comment is a question about the code, not a judgement of you.',
      ),
      reviewing: beats(
        'Reviewing is the harder skill. Read for intent before style.',
        'Say whether you mean this is broken, this is a suggestion, or this is only my taste.',
        'And approve things. The most useful sentence in a review is: I might be missing something, but.',
      ),
      chapter: beats(
        'Four levels, in both directions: answering a review, and giving one.',
        'Then shaping a change so it can be reviewed, and catching up a branch that fell behind.',
        'This is the chapter that makes you someone a team can work with. Start by answering a review.',
      ),
    },
    fig: {
      you: 'you',
      reviewer: 'a reviewer',
      everyone: 'everyone, every level',
      inReview: 'in review: minutes',
      afterRelease: 'after release: an incident',
      knowledge: 'two people know it',
      small: 'small',
      huge: '1,000 lines',
      whyText: 'why',
      broken: 'broken',
      suggestion: 'suggestion',
      taste: 'taste',
      approved: 'approved',
    },
  },
  /* --- World 08 · The Codebase -------------------------------------------- */

  codebase: {
    narration: {
      blank: beats(
        'Every exercise you have ever done started from a blank file.',
        'Almost no professional work is like that. You join a codebase that is ten or twenty years old, written by people who have left.',
        'You will spend far more of your career reading code than writing it. It is a separate skill, and it is the job.',
      ),
      fence: beats(
        'The first reaction to strange code is contempt: who wrote this?',
        'Far more often you are looking at history: a deadline, one customer’s need, a library bug in 2019.',
        'The old rule is Chesterton’s fence: do not tear a fence down until you know why it was put there.',
      ),
      trail: beats(
        'Git can tell you, for any line, which commit brought it in, who wrote it, when, and why.',
        'From the line, to the commit, to the message, to the ticket.',
        'Quite often, ten words explain everything. You stop guessing at intent and start looking it up.',
      ),
      scale: beats(
        'Renaming something everywhere is not find and replace. You have to know exactly what you touched.',
        'Dead code is not free. The fear is, what if something needs it? It is committed. It is recoverable. Delete it.',
        'And when one module is doing four jobs, split it, so a change to one stops risking the other three.',
      ),
      separate: beats(
        'All three are changes with no new behaviour. Nothing the user sees is different.',
        'That makes them easy to review, which is why they are kept apart from changes that do alter behaviour.',
        'A commit that renames forty things and also fixes a bug is a commit nobody can review properly.',
      ),
      chapter: beats(
        'Four levels, all inside code you did not write: following a line back to its reason, and renaming across a whole project.',
        'Then removing dead code with confidence, and splitting a module that grew too big.',
        'By the end, an unfamiliar codebase should feel less like a wall and more like a place with records.',
      ),
    },
    fig: {
      years: '20 years',
      reading: 'reading',
      writing: 'writing',
      why: 'why?',
      line: 'the line',
      commit: 'the commit',
      message: 'the message',
      ticket: 'the ticket',
      rename: 'rename',
      dead: 'dead code',
      split: 'split',
      noBehaviour: 'no new behaviour',
      tidy: 'tidy',
      fix: 'fix',
    },
  },
  /* --- World 09 · Releases ------------------------------------------------ */

  releases: {
    narration: {
      line: beats(
        'Everything so far happened inside a repository, and the number of people you could hurt was one.',
        'A release says: this exact state of the code is the one real people now use.',
        'From that moment, a bug is somebody’s afternoon. And shipping is fast, but unshipping is not.',
      ),
      tag: beats(
        'Every release gets a tag: a permanent label on one exact commit.',
        'Version two point four point one means one state of the code, forever, not roughly what was on main that week.',
        'When a customer reports a bug, the first question is which version, and the answer must be something you can run.',
      ),
      semver: beats(
        'Three numbers, and each one is a promise to whoever depends on you.',
        'The last means I fixed something. The middle means I added something, and your use still works.',
        'The first means I broke something: read the notes before you upgrade.',
      ),
      notes: beats(
        'Beside the tag goes a changelog, written for people, not engineers.',
        'Refactor the order processor tells a user nothing.',
        'Orders placed in the final minute of a sale are no longer rejected. It is the same change, and now they can tell if it affects them.',
      ),
      hotfix: beats(
        'Something is broken in production, and main has moved on with other people’s unfinished work.',
        'So you go back to the released version, make the smallest fix there, and release that.',
        'Then you bring the fix back to main, or the next release quietly undoes it.',
      ),
      rollback: beats(
        'The braver instinct is the rollback: put the last good version back first, and find out why afterwards.',
        'Stopping the harm is not the same as fixing the problem, and doing them in that order is a mark of experience.',
        'It only works because somebody tagged that last good version.',
      ),
      chapter: beats(
        'Four levels: marking a release, and writing the notes in words people use.',
        'Then fixing a released version while main has moved on, and rolling a bad release back.',
        'This is where the work stops being an exercise. Start by marking a release.',
      ),
    },
    fig: {
      repository: 'repository',
      users: 'real users',
      patch: 'fixed',
      minor: 'added',
      major: 'broke',
      forUsers: 'for users',
      released: 'released',
      theFix: 'the fix',
      lastGood: 'last good',
      bad: 'bad',
    },
  },
  /* --- World 10 · Production ---------------------------------------------- */

  production: {
    narration: {
      night: beats(
        'It is two in the morning. Something that was working has stopped, and people are affected right now.',
        'Senior engineers handle this well mostly not because they know the system better, but because they have a procedure.',
        'It is built for exactly how you feel at that hour: frightened, tired, and ready to guess.',
      ),
      bleeding: beats(
        'Restoring service and fixing the cause are two different jobs, and they happen in that order.',
        'If rolling back to yesterday makes it work, roll back, even though you do not know why yet.',
        'Once the bleeding has stopped, you can investigate calmly, with the evidence, and without a clock.',
      ),
      logs: beats(
        'Then, evidence, not theories. A running system leaves a trail, and reading it means narrowing.',
        'From the site is down, to this endpoint fails after this deploy, to this call throws when a field is missing.',
        'Every step should rest on something you saw, not something you suspect.',
      ),
      reproduce: beats(
        'The most valuable moment in any incident is when you can make the failure happen on demand.',
        'Before that, you have a story. After it, you have something to test.',
        'Without it, you fix things that were never the cause, and the problem is back an hour later.',
      ),
      postmortem: beats(
        'Afterwards, good teams write it down: what happened, when, why, and what will change.',
        'And they write it blamelessly: everyone acted reasonably, so why did the system make that dangerous?',
        'Someone deployed without checking is a dead end. It was possible to deploy without checking is a finding, and it has a fix.',
      ),
      chapter: beats(
        'Four levels, following one incident from start to end.',
        'Reading the logs, reproducing the failure, stopping the bleeding, and writing the account.',
        'It is meant to feel like a bad night at a real job, so the second time is not your first. Start with the logs.',
      ),
    },
    fig: {
      broken: 'down',
      restore: 'restore',
      diagnose: 'diagnose',
      onDemand: 'on demand',
      timeline: 'timeline',
      why: 'why',
      change: 'what changes',
    },
  },
  /* --- World 11 · The Pipeline -------------------------------------------- */

  pipeline: {
    narration: {
      discipline: beats(
        'Ask a team how they keep quality up, and you hear a list: run the tests, check the formatting, do not commit that file.',
        'None of it survives a Thursday afternoon before a holiday. People are tired, new, in a hurry.',
        'So if a rule matters, a machine should enforce it. Not as a comment on anyone, just arithmetic.',
      ),
      history: beats(
        'Teams once worked apart for weeks, then merged everything near a deadline.',
        'Every incompatibility surfaced at once. It had a name, integration hell, and it could eat a fifth of a schedule.',
        'The fix was to merge constantly, with an automatic check every time. Two hours of conflict, instead of two months.',
      ),
      command: beats(
        'The first layer is one command: test, or check, or build, instead of a paragraph in a readme that has been wrong since March.',
        'It is the foundation for everything after it.',
        'And it is the kindest thing you can do for the next person who joins.',
      ),
      hook: beats(
        'The second layer is a hook. Git runs that command for you, before a commit is made.',
        'Now the check happens without anyone choosing to run it.',
        'Then continuous integration runs it on a clean machine, for every change, for everyone. It works on my machine is usually true, and that is the problem.',
      ),
      flaky: beats(
        'A flaky test passes sometimes and fails sometimes, and it is worse than a failure.',
        'It teaches the team that a red build might mean nothing, so people just run it again.',
        'A reliable check that catches ninety percent is worth more than an unreliable one that catches everything. Trust is what it runs on.',
      ),
      chapter: beats(
        'Four levels: one command anyone can run, and a hook that runs it for you.',
        'Then making a random failure deterministic, and a pipeline that checks every change, forever.',
        'Automation is a way of helping people who are not in the room. Start with the one command.',
      ),
    },
    fig: {
      tests: 'run the tests',
      format: 'check formatting',
      notThatFile: 'not that file',
      weeks: 'weeks apart',
      daily: 'every day',
      beforeCommit: 'before a commit',
      yourMachine: 'your machine',
      cleanMachine: 'a clean machine',
      rerun: 'run again',
    },
  },
  /* --- World 12 · Data ---------------------------------------------------- */

  data: {
    narration: {
      net: beats(
        'Every chapter so far had a safety net. Commit early, delete boldly, because you can always get back.',
        'That was true because git keeps copies of code. It does not keep copies of your data.',
        'Change a production database and there is no previous commit to check out. Only a backup, if one works.',
      ),
      twice: beats(
        'Data is harder for three reasons. First, running a deploy twice is harmless. Adding ten percent to every price twice is not.',
        'Scripts time out and get restarted, and retries fire. So every change must be safe to run again.',
        'Second, for a while, old servers and new servers both run against one database.',
      ),
      stages: beats(
        'So a change is staged: first a shape that both versions can live with.',
        'Then move the data, and deploy the new code.',
        'Only then remove the old shape. Three deploys to do safely what looks like one.',
      ),
      batches: beats(
        'Third, it is big enough to hurt. Instant on a hundred rows can lock a table for twenty minutes on a hundred million.',
        'So you work in batches, and you can stop and resume.',
        'That stops being a nicety, and becomes the only way that works.',
      ),
      habits: beats(
        'Know exactly how you would undo it before you run it. Restore from backup is a hope, not a plan.',
        'Never let a destructive step come first. Copy, verify, and only days later, delete.',
        'Count before and after. You meant to touch four thousand rows, and you touched ninety thousand.',
      ),
      chapter: beats(
        'Three levels, and they are the ones that go wrong in real life.',
        'Reshaping data under running code, filling a large table in batches, and a change that would quietly destroy data.',
        'Everywhere else the advice was to experiment. Here it is the opposite: when it cannot be undone, slow down.',
      ),
    },
    fig: {
      code: 'code',
      data: 'data',
      backup: 'backup?',
      runTwice: 'run twice',
      oldCode: 'old code',
      newCode: 'new code',
      expand: 'add',
      migrate: 'move',
      contract: 'remove',
      rows: '100,000,000 rows',
      undo: 'undo',
      copy: 'copy',
      verify: 'verify',
      remove: 'delete',
      expected: 'expected',
      touched: 'touched',
    },
  },
  /* --- World 13 · Design -------------------------------------------------- */

  design: {
    narration: {
      unknown: beats(
        'So far most problems had a correct answer: the test passes, or it does not.',
        'Some changes are different. Will this checkout convert better? Will this be faster with real traffic? Nobody knows.',
        'The skill is not writing the change. It is arranging things so that being wrong is cheap.',
      ),
      flag: beats(
        'Shipping the code and turning the feature on do not have to be the same moment.',
        'Put the new behaviour behind a flag, off by default. It can be merged and deployed while doing nothing at all.',
        'Then turn it on for yourself, then the team, then one percent of users, then everyone.',
      ),
      off: beats(
        'If it goes wrong at any point, you turn it off. Seconds, with no deploy and no incident.',
        'But every flag is a second version of your system that somebody has to reason about.',
        'So once the question is answered, the flag comes out.',
      ),
      contract: beats(
        'The moment anything depends on your code, the shape of that interface is a promise, whether you remember making it or not.',
        'Rename a field, and your tests pass, and somebody running an app from 2023 breaks.',
        'So you add rather than alter, keep the old path working beside the new, and give people a way to move.',
      ),
      measure: beats(
        'Engineers are confidently bad at guessing where a program spends its time.',
        'It is rarely the clever algorithm. It is a query in a loop, or a file read nobody noticed.',
        'So measure first, and know what fast enough means before you start, or you never stop.',
      ),
      chapter: beats(
        'Four levels, each a risky change made safe by structure, not by care.',
        'A flag, an interface changed without breaking anyone, something made measurably faster, and knowing which changes need this.',
        'It is the first chapter about judgement more than technique. Start with the flag.',
      ),
    },
    fig: {
      pass: 'pass',
      fail: 'fail',
      unknown: '?',
      deployed: 'deployed',
      on: 'on',
      off: 'off',
      you: 'you',
      team: 'team',
      everyone: 'everyone',
      yourTests: 'your tests',
      theirApp: 'their app',
      guess: 'guess',
      measured: 'measured',
      enough: 'fast enough',
      algorithm: 'the algorithm',
      queryLoop: 'a query in a loop',
      fileRead: 'a file read',
    },
  },
  /* --- World 14 · Ownership ----------------------------------------------- */

  ownership: {
    narration: {
      scope: beats(
        'The ladder in this profession does not really measure skill. It measures what you are accountable for.',
        'A junior engineer owns their tickets. A senior engineer owns a system, at the weekend, even the parts they never chose.',
        'Ownership is not a reward. It goes to the people who fix what is broken and unowned, instead of noting it is not theirs.',
      ),
      dependency: beats(
        'Adding a library feels free. One line, and you move on this afternoon.',
        'But you inherit its bugs, its security holes, its release schedule, and its own dependencies, maybe two hundred of them.',
        'So the question is not, does this work? It is, is this worth owning, counted over years, not afternoons.',
      ),
      runbook: beats(
        'A runbook says what this thing is, how it fails, what to check first, how to restart it, and who to wake.',
        'It is written for someone capable, under pressure, at three in the morning, who has never touched it. Short, in order, real commands.',
        'Its test is whether someone else can follow it alone. And it is how you stop being the only one who can keep it alive.',
      ),
      oncall: beats(
        'The course ends on a night on call. The system runs whether you watch or not, and if it breaks, you are woken.',
        'Everything arrives at once: the logs, the rollback, the cause, the fix, and the account.',
        'Every habit in this course was a message sent forward to whoever holds the pager. Quite often, that is you.',
      ),
      chapter: beats(
        'Three levels that ask for judgement, not knowledge: saying no to a dependency, and why.',
        'Writing the document that keeps your system alive without you, and one night on call.',
        'It will not make you senior. But you will have done, once, everything the job is made of. You will recognise it.',
      ),
    },
    fig: {
      tickets: 'tickets',
      system: 'a system',
      oneLine: 'one line',
      whatFails: 'how it fails',
      checkFirst: 'check first',
      restart: 'restart',
      wake: 'who to wake',
    },
  },
  /* --- World 15 · The Platform -------------------------------------------- */

  platform: {
    narration: {
      guest: beats(
        'For fifty levels, the repository was yours. That ends in the first week of a real job.',
        'What blocks you at work is almost never in your own code. It is a bug in a library three teams over.',
        'You can read all of it, and change your own copy, and none of that gets your fix to where it runs.',
      ),
      history: beats(
        'In 2005, Linus Torvalds wrote git, and every clone was a complete repository. Git has never known who you are.',
        'So every question about who may change what had to be answered by something standing in front of git.',
        'In 2008, GitHub answered it with two things: the fork button and the pull request page.',
      ),
      fork: beats(
        'A fork is a copy of the permission, not of the code. It is a repository on the server that you may write to.',
        'A pull request is a message between two repositories: here is a branch, and here is why you want it.',
        'Opening one moves nothing. Someone with access to the other side decides, and they can say no.',
      ),
      protect: beats(
        'Then teams learned that a rule in a wiki is not a control.',
        'A protected branch is a program on the server that runs before anything moves. When your push is refused, nothing was written.',
        'A required check makes the test result binding, on everybody, including the person who wrote the check.',
      ),
      you: beats(
        'Send another team a clean, tested, one-commit change with four sentences of context, and the fix lands this week.',
        'And from the other side, the person most likely to push something unreviewed to your main is you, in a hurry.',
        'A boundary that exempts its author fails exactly when it is needed.',
      ),
      chapter: beats(
        'Two levels, one from each side of the boundary.',
        'First, fix a bug in a library you cannot push to. Then protect a branch, and watch it stop a push, even yours.',
        'Who is allowed to change this, and what happens when someone who is not tries? Start with the fork.',
      ),
    },
    fig: {
      yours: 'yours',
      theirs: 'theirs',
      noUsers: 'no users',
      fork: 'your fork',
      upstream: 'their repository',
      refused: 'refused',
      check: 'check',
      you: 'you',
    },
  },
  /* --- World 16 · The Program --------------------------------------------- */

  program: {
    narration: {
      lie: beats(
        'Something is wrong in production. The code is right, the branch is right, and the tests pass.',
        'Every file you know how to read says the system is fine, and it is not.',
        'The thing that is running is not the thing you are reading.',
      ),
      copy: beats(
        'A running program is a copy, taken when it started, living in memory.',
        'It has no name, it is not in version control, and it leaves no history.',
        'Change the file, or even delete it, and the process carries on exactly as before.',
      ),
      fork: beats(
        'Unix, from 1969, made a process in two steps: copy an existing one, then replace the program inside the copy.',
        'It looked wasteful. But in the gap between the two, you can change where the new process stands and where its output goes.',
        'Every pipe, every redirect, every background job you have typed lives in that gap.',
      ),
      signals: beats(
        'A process is told things with signals: a number, with no message attached.',
        'Almost every signal is a request. A program may answer it, delay it, or ignore it.',
        'Two cannot be caught. Kill and stop are handled by the kernel alone, and the program is never told.',
      ),
      parts: beats(
        'A process is four things: the program, its own memory, its connections, and a number.',
        'Its memory holds everything not yet written. That is why stopping a process is a decision, not a formality.',
        'And the number is only an index. When the process ends, the kernel hands it to something else.',
      ),
      chapter: beats(
        'This chapter takes you out of git for the first time, onto a machine.',
        'Something is charging the wrong prices and the repository cannot explain it. Then two jobs must come off a box before it is rebuilt.',
        'One holds orders that exist in no file. One was asked to stop, and decided not to. Start with what is running.',
      ),
    },
    fig: {
      reading: 'what you read',
      running: 'what is running',
      memory: 'memory',
      copyStep: 'copy',
      replace: 'replace',
      request: 'a request',
      kernel: 'kernel',
      program: 'program',
      ownMemory: 'memory',
      connections: 'connections',
      number: 'number',
    },
  },
  /* --- World 17 · The Network --------------------------------------------- */

  network: {
    narration: {
      elsewhere: beats(
        'Until now, everything you debugged was in front of you: the repository, the process, even the file with no name.',
        'A network call is the first time the thing is somewhere else, under somebody else’s control.',
        'You cannot read its logs, or often its source. What you can do is ask it, carefully, and read the answer properly.',
      ),
      library: beats(
        'Most engineers are bad at this, not because it is hard.',
        'There is almost always a client library in the way, turning the whole conversation into one line that works or throws.',
        'And when it throws, the stack trace is about the library.',
      ),
      history: beats(
        'HTTP was designed in 1989 at CERN, so physicists could share documents. The first version was about twenty lines.',
        'In 1991 came the decision that made it last: the protocol is text, and anyone can add a header.',
        'So you can read it, and type it by hand. That is how people really debug it.',
      ),
      stateless: beats(
        'The server does not remember you between requests.',
        'Who you are, what you accept, what you are sending, whether this is a retry: it all rides in the headers, every time.',
        'That is why so much goes wrong there, and so few people look.',
      ),
      anatomy: beats(
        'A response is three things: a status line, the headers, and the body.',
        'The body can be perfect while the headers make it unusable. The status can say two hundred while the body says it failed.',
        'A client library folds all three into one verdict, and usually the wrong one.',
      ),
      chapter: beats(
        'Four levels, against a real service on this machine, and you talk to it by hand before you change anything.',
        'The right data served in a way no client can use, what the numbers mean, a call that never comes back, and a retry done properly.',
        'None of it needs the internet. All of it is how the internet works. Start by asking the server yourself.',
      ),
    },
    fig: {
      you: 'you',
      server: 'their server',
      library: 'library',
      status: 'status',
      headers: 'headers',
      body: 'body',
    },
  },
  /* --- World 18 · The Database -------------------------------------------- */

  database: {
    narration: {
      outlives: beats(
        'Services get rewritten, languages get swapped, and the box gets rebuilt. The data stays.',
        'A bug in a service is a bad afternoon. A bug that writes wrong data is a bad year.',
        'So ask the database yourself, not a script that asked it three weeks ago.',
      ),
      codd: beats(
        'In 1970, Edgar Codd at IBM was annoyed that every database made you know where the data was.',
        'He proposed tables, and a new bargain: you ask for what you want, and the system decides how to get it.',
        'You give up control of how, and in exchange you may ask anything. That is still what SQL looks like.',
      ),
      slow: beats(
        'Almost nothing in a database is slow because the computer is slow. The number of rows it looks at is wrong.',
        'It read every row when it could have read ten, because nothing told it where to look.',
        'Or it asked a thousand times for what it could have asked once, or it waited on a lock someone else held.',
      ),
      plan: beats(
        'Because the database chooses the route, slowness is hidden inside a choice you did not make.',
        'So you learn to see the plan it chose, and ask it to explain.',
        'Then you change the information it chose with, often by adding one index.',
      ),
      writes: beats(
        'The last kind of bug does damage: writes where some happened and some did not.',
        'A payment taken with no order recorded. An order with no items. Stock counted down twice.',
        'The database has a way to make that impossible: all of it happens, or none of it does.',
      ),
      chapter: beats(
        'Four levels against a real database on this box, with real data in it.',
        'Answer questions by asking the data, fix a page that got slow, find the loop behind a thousand queries, and make half-finished writes whole.',
        'The commands are sqlite and SQL, and both are the real thing. Start by asking the data a question.',
      ),
    },
    fig: {
      service: 'service',
      data: 'data',
      where: 'where',
      what: 'what',
      everyRow: 'every row',
      allOrNone: 'all or none',
    },
  },
  /* --- World 19 · The API ------------------------------------------------- */

  api: {
    narration: {
      weather: beats(
        'Until now you were the client, and the API was the weather.',
        'Turn around. Somebody else is the client, and you are the weather.',
        'Your own code can be rewritten this afternoon. A published interface cannot, because someone you have never met is written against it.',
      ),
      procedure: beats(
        'For years, programs talked over HTTP by sending a procedure call to a single address.',
        'One endpoint, an envelope naming the function to run, and its return value sent back.',
        'It looked exactly like the code you were already writing.',
      ),
      fielding: beats(
        'In 2000, Roy Fielding argued that HTTP already had a vocabulary, and ignoring it threw the web away.',
        'An address names a thing. A read is understood by every cache on the way to be safe to repeat.',
        'Hide everything inside one envelope, and nothing can be cached, and nothing can be guessed.',
      ),
      nouns: beats(
        'So most APIs you meet now are nouns you can address, with a small, fixed set of verbs.',
        'Not because nouns are elegant.',
        'Because everything between you and your caller already knows what they mean.',
      ),
      wrong: beats(
        'What actually goes wrong: the shape copies a table, and every caller is now tied to it.',
        'A change nobody could be using breaks three people, because you only see the callers who complain.',
        'A response that was fine with forty rows times out at forty thousand. And a failure the caller cannot read leaves only a spinner.',
      ),
      chapter: beats(
        'Four levels, against the storefront’s own API, with a mobile app and a partner already calling it.',
        'Give it a shape someone can guess, change it without breaking anyone, page through data without skipping rows, and describe failures.',
        'Before you ship it, ask what it would cost to change it. Start with the endpoint.',
      ),
    },
    fig: {
      client: 'client',
      you: 'you',
      cache: 'cache',
    },
  },
  /* --- World 20 · Security ------------------------------------------------ */

  security: {
    narration: {
      adversary: beats(
        'A slow query is not trying. A full disk did not work out that you never check the return value.',
        'This chapter is different in one way: there is somebody on the other side, and they are looking.',
        'They read your history, not just its tip. They send the input you did not imagine, and they try the address with someone else’s id in it.',
      ),
      edge: beats(
        'For years, security lived at the edge of the network, and the application inside could trust whatever reached it.',
        'Then the application became the edge. In 1998, a note described pasting a web form into a SQL statement: SQL injection.',
        'Since then the lesson keeps repeating. Most real break-ins come through the front door, in a request that looks valid.',
      ),
      history: beats(
        'In 2017, Equifax lost the records of about a hundred and forty seven million people through a hole whose patch had existed for two months.',
        'In 2021, a logging library most engineers had never chosen, only inherited, turned a username into code running on their servers.',
        'And every year, the commonest way in is the least dramatic: a working password somebody left where it could be found.',
      ),
      shapes: beats(
        'It comes in four shapes. A credential ends up where it can be read, and deleting it does nothing, because the history kept it.',
        'Input is treated as instruction: a search box pasted into something that runs.',
        'Logged in is mistaken for allowed: change the id in the address, and you get someone else’s invoice. And a library you depend on has a published hole.',
      ),
      boring: beats(
        'Each one is boring.',
        'Each has a fix that takes minutes.',
        'And every one has cost a company you have heard of more than its engineers cost in a year.',
      ),
      chapter: beats(
        'Four levels, one shape each, in the storefront you already know.',
        'A key in the history, a search box that runs a command, a real login reading what is not yours, and an advisory to act on by the end of the day.',
        'None of it needs cleverness. Assume, once, that somebody is looking, and read your work the way they would.',
      ),
    },
    fig: {
      indifferent: 'indifferent',
      looking: 'looking',
      network: 'network',
      app: 'application',
      history: 'history',
      search: 'search',
      loggedIn: 'logged in',
      allowed: 'allowed',
      minutes: 'minutes',
    },
  },
  /* --- World 21 · Concurrency --------------------------------------------- */

  concurrency: {
    narration: {
      alone: beats(
        'Every function you have written assumes that between one line and the next, nothing happened.',
        'It was never true. Your code runs beside copies of itself, on eight cores, and between any two lines the rest of the machine happens.',
        'Most of the time you get away with it. That is the problem: the bug happens sometimes, under load, on the day of the sale.',
      ),
      lunch: beats(
        'For twenty five years, the fix for slow code was to wait, because chips got faster every two years.',
        'Around 2004 that stopped. Chips got more cores instead, and the free lunch was over.',
        'Doing more than one thing at once became every programmer’s problem, not a specialist’s.',
      ),
      history: beats(
        'The theory was already old. In 1965, Dijkstra asked how several processes agree that only one of them goes first.',
        'And the failures came early. A radiation therapy machine had a race that only appeared when an operator typed quickly.',
        'In 2003, a race in alarm software meant nobody saw the first failure, and fifty five million people lost power.',
      ),
      shapes: beats(
        'Four shapes. Two readers see three left, both decide there is enough, both write two. You sold eight, and stock fell by one.',
        'You check that a file does not exist, then create it, and someone else created it in between.',
        'Two jobs each hold what the other wants, and wait forever. Or a job ran, the acknowledgement was lost, and it runs again.',
      ),
      chance: beats(
        'None of the fixes is hard. Each is a line or two.',
        'What is hard is that the failure is a matter of chance.',
        'A green test only proves the bug did not happen this time, and the bug that did not happen this time is exactly the bug.',
      ),
      chapter: beats(
        'Four levels, one shape each, on a shop that is having a sale.',
        'Overselling the last three, making two jobs take turns, moving work onto a queue, and a job the queue runs twice.',
        'None of it is about threads. It is noticing the moment you assumed you were alone. Start with two at once.',
      ),
    },
    fig: {
      cores: '8 cores',
      sold: 'sold',
      stock: 'stock',
      waits: 'waits',
      twice: 'twice',
    },
  },
  /* --- World 22 · Observability ------------------------------------------- */

  observability: {
    narration: {
      debugger: beats(
        'Every debugging habit you have was formed somewhere you could stop the program and run it again.',
        'Production is not like that. It happened at three fourteen in the morning, to one customer, on one of nine servers, and it is not happening now.',
        'So all you know is what you decided, in advance, to write down. Often that was decided by accident, four years ago.',
      ),
      kinds: beats(
        'Logs are wonderful for one event, and useless for a million.',
        'Metrics are numbers, added up as they happen, cheap enough to keep forever.',
        'Traces follow one request across many services, and show where it spent its time.',
      ),
      golden: beats(
        'In 2016, Google’s reliability book named the few things worth measuring about almost anything.',
        'Latency, traffic, errors, and saturation: the four golden signals.',
        'And the mistake that outlives every tool is using the wrong kind: searching logs for a trend, or a dashboard for one customer.',
      ),
      average: beats(
        'The average hides the customer who left. The mean is a hundred and eighty milliseconds, and the slowest one in a hundred takes four seconds.',
        'A metric split by customer id is not one metric but a million, and it brings the system down during the incident.',
        'The trace stops at the boundary, and the time is in the gap nobody measures.',
      ),
      alerts: beats(
        'Then the alert that fires on something that is not a problem.',
        'The processor is at ninety percent, which is fine, and it wakes someone at four in the morning.',
        'Do that enough, and nobody reads the pages any more, which is worse than having none.',
      ),
      chapter: beats(
        'Three levels, on a storefront with real traffic.',
        'A dashboard that says checkout is fast when it is slow, one request through four services, and a pager that went off two hundred times this month.',
        'None of it is about installing anything. It is deciding, before the bad night, what the system should be able to tell you.',
      ),
    },
    fig: {
      logs: 'logs',
      metrics: 'metrics',
      traces: 'traces',
      latency: 'latency',
      traffic: 'traffic',
      errors: 'errors',
      saturation: 'saturation',
      mean: 'mean',
      gap: 'the gap',
    },
  },
  /* --- World 23 · Scale and Cost ------------------------------------------ */

  scaleCost: {
    narration: {
      bottleneck: beats(
        'At any moment, one thing is the limit: a database, a lock, one slow call in the middle of a loop.',
        'Everything else has slack. Make anything with slack faster, and the system runs exactly as fast as before.',
        'In 1967, Gene Amdahl wrote down the arithmetic: make a quarter of the work infinitely fast, and the whole is a third faster, never more.',
      ),
      find: beats(
        'So the first move is never to make something faster.',
        'It is to find out what is actually waiting.',
        'If you do not know which part is the limit, your odds of picking it are bad, and they get worse with every part.',
      ),
      queue: beats(
        'In 1961, John Little proved that what is inside a system equals how fast things arrive, times how long each one stays.',
        'Near full capacity, waiting does not rise smoothly. It goes vertical.',
        'At ninety five percent, queues are not four times longer than at seventy. They are twenty times longer.',
      ),
      headroom: beats(
        'That is why we are only at ninety percent, we have room, is one of the most expensive sentences in the business.',
        'For thirty years, capacity was a purchase order and a rack, paid once for the peak.',
        'Now it is billed by the second, and it changes when you change a loop. The price of a design can be measured, so it can be wrong.',
      ),
      wrong: beats(
        'Cache with the wrong key, and one customer is served another customer’s data.',
        'Load test a health check at ten thousand requests a second, and the report says everything is fine.',
        'And a query run once per row is a slow page on your laptop, and an invoice in production.',
      ),
      chapter: beats(
        'Three levels, on a shop that has started to be busy.',
        'A slow page made fast by not asking twice, a thousand requests to find where it stops being linear, and the cost of a feature per order.',
        'Measure before you change anything, know that only one thing is the limit, and say what your design costs in money. Start by not asking twice.',
      ),
    },
    fig: {
      limit: 'the limit',
      slack: 'slack',
      capacity: 'capacity',
      waiting: 'waiting',
      rack: 'a rack',
      perSecond: 'per second',
      yourLaptop: 'your laptop',
      invoice: 'invoice',
    },
  },

  /* --- The landing page · Why engineering, now ---------------------------- */

  /* Not a chapter opener: the pitch, told the way the openers tell an essay.
     Every claim in it is one the landing page already makes and can stand
     behind (docs/ai-era-plan.md, strings/credibility.ts `proof`), so it says
     "more than a hundred levels" rather than a count that moves when a
     chapter ships. */
  whyEngineering: {
    narration: {
      job: beats(
        'Knowing whether code is right has always been the real job of a software engineer.',
        'It means reading code you did not write, finding why a test fails, and knowing what a change will break.',
        'AI makes typing cheaper. It makes that judgment worth more.',
      ),
      workplace: beats(
        'EngineerOS teaches it the way the job does: by doing it.',
        'You join Lattice, a fictional company, and work in its real repository, with a real Linux running in your browser tab.',
        'Your teammates hand you broken builds, lost work and pull requests to review.',
      ),
      teammates: beats(
        'One teammate is Kite, a coding agent whose work looks finished, even when it is not.',
        'You learn to review its work just as you would review any teammate.',
        'And Iris is your own agent. Brief her well and check her work, because like any agent, she is sometimes wrong.',
      ),
      graded: beats(
        'You are graded on the state you leave the repository in, not on the commands you type.',
        'So use any tool you like, AI included. What counts is that the work is right.',
        'Checkpoints test the whole job with no hints, and a verified pass goes on a public page you can share.',
      ),
      path: beats(
        'The course starts with your first command in a terminal and goes all the way to building with AI models.',
        'There are more than a hundred levels, each one a situation engineers meet at work.',
        'The first levels are free, and level zero takes about four minutes.',
        'Learn engineering for the AI era.',
      ),
    },
    fig: {
      you: 'you',
      read: 'read others’ code',
      diagnose: 'find why it fails',
      predict: 'know what breaks',
      typing: 'typing',
      judgment: 'judgment',
      realLinux: 'a real Linux, in your browser tab',
      buildFailing: 'build failing',
      lostWork: 'my work is gone',
      pleaseReview: 'please review',
      codingAgent: 'a coding agent',
      testsPass: 'tests pass',
      missedCase: 'a case it missed',
      yourReview: 'your review',
      irisBrief: 'brief her · check her work',
      whatYouTyped: 'what you typed',
      notGraded: 'not graded',
      theState: 'the state you leave',
      ai: 'AI',
      anyTool: 'any tool',
      checkpoint: 'checkpoint · no hints',
      verified: 'verified',
      firstCommand: 'first command',
      tests: 'tests',
      production: 'production',
      aiModels: 'AI models',
      moreThan100: 'more than 100 levels',
      free: 'free to start',
      fourMinutes: 'first level · ~4 min',
      tagline: 'Learn engineering for the AI era.',
    },
  },
  /* --- The landing page: the swimmer and the ship ------------------------
     Why learn software engineering in the age of AI? The YouTube special of
     the history series (videos/software-history/swimmer-and-ship), above the
     course pitch. English is the video's recording; Arabic plays silently. */
  swimmerShip: {
    narration: {
      hook: beats(
        'Picture everything software can do as an ocean.',
        'For seventy years, engineers explored it the only way there was: by swimming. Every line of code was a stroke you made yourself.',
        'Now we have something new. We have a ship.',
      ),
      swimmer: beats(
        'In August 1875, a merchant navy captain named Matthew Webb became the first person on record to swim the English Channel.',
        'It took him almost twenty-two hours. The tides kept pushing him sideways, so his path was nearly twice the straight distance.',
        'That is how a swimmer learns the water: the cold, the currents, where the tide wants to take you.',
        'Engineers who wrote every line by hand learned their systems the same way. Slowly, and very well.',
      ),
      ship: beats(
        'In December 1872, HMS Challenger sailed from England on what is often called the first great voyage of ocean science.',
        'Over three and a half years it covered about a hundred and thirty thousand kilometres. Southwest of the Mariana Islands, its crew lowered a weighted line more than eight kilometres down.',
        'No swimmer could ever go there. Ships did not just make the old trips faster. They reached places no one had been.',
        'AI agents are our ship. You choose where to go, and the agent reads, writes and runs code, farther and faster than any of us could alone.',
      ),
      captain: beats(
        'But a ship does not choose where to go. It goes where it is pointed, and it goes fast.',
        'Navigators have a rule of thumb: one degree off course, and you end up about one kilometre off for every sixty you travel.',
        'A swimmer who drifts is a few metres wrong. A ship that drifts reaches the wrong coast. An agent that drifts can write a thousand wrong lines before lunch.',
        "So navigators check their position again and again, and correct. Engineers check an agent's work the same way: read it, test it, and prove it works.",
      ),
      swim: beats(
        'Even today, every recruit in the US Navy has to pass a swimming test to finish boot camp.',
        'Not to swim across oceans. Because far out at sea, when something goes wrong, knowing the water is what brings you home.',
        'Agents get stuck. Sometimes they are confidently wrong. Then you need to read the code yourself, find the bug, and fix it by hand.',
        "Matthew Webb was a ship's captain who could swim. The ship takes you farther from shore. That is exactly why you still need to swim.",
      ),
      close: beats(
        'In January 1960, the Trieste carried two people almost eleven kilometres down, to the deepest point of the ocean, a place now called the Challenger Deep.',
        'The machine did the diving. The people inside it read every gauge and made every decision.',
        "That is the engineer's job now. Learn to swim. Then learn to sail.",
        'The ocean has not got any smaller. But now we can reach far more of it.',
      ),
    },
    fig: {
      shore: 'shore',
      everything: 'everything software can do',
      eachStroke: 'each stroke: one line of code',
      swimRange: 'as far as one person can swim',
      aShip: 'a ship',
      webb: 'Captain Matthew Webb',
      dover: 'Dover',
      calais: 'Calais',
      straight: '34 km straight',
      swimTime: '21 h 45 min',
      tide: 'tide',
      swum: 'about 64 km swum',
      cold: 'the cold',
      currents: 'the currents',
      tidePull: 'the tide’s pull',
      howItFails: 'how it fails',
      whereSlow: 'where it is slow',
      whyBuilt: 'why it is built this way',
      aTask: 'a task',
      done: 'done',
      slowly: 'slowly, and very well',
      challenger: 'HMS Challenger',
      voyage: 'December 1872 – May 1876',
      distance: '≈ 130,000 km',
      surface: 'surface',
      /** The unit after a depth, written by the scene as `8,184 m`. */
      metres: 'm',
      diver: 'a diver: tens of metres',
      hairline: 'a hairline at this scale',
      noOne1: 'places no one',
      noOne2: 'had been',
      swimmersRange: 'the swimmer’s range',
      theAgent: 'the agent',
      youWhere: 'you: where to go',
      reads: 'reads',
      writes: 'writes',
      runsCode: 'runs code',
      fartherFaster: 'farther and faster',
      navigator: 'a navigator takes a sight',
      navigatorCredit: 'USS Alaska, 1945, US Navy',
      pointed: 'where it is pointed',
      oneDegree: '1° off',
      km60: '60 km',
      off1: '1 km off',
      off10: '10 km off after 600 km',
      notToScale: 'gap drawn larger than scale',
      aSwimmer: 'a swimmer',
      fewMetres: 'a few metres wrong',
      wrongCoast: 'the wrong coast',
      anAgent: 'an agent',
      thousandLines: 'a thousand wrong lines',
      checkCorrect: 'check the position, correct',
      readIt: 'read it',
      testIt: 'test it',
      proveIt: 'prove it works',
      fixesOnAgent: 'an engineer’s checks on an agent’s work',
      swimTest: 'sailors at a swim test',
      swimTestCredit: 'Pearl Harbor, 2013, US Navy',
      everyRecruit: 'every recruit: pass a swim test',
      goesWrong: 'something goes wrong',
      knowingWater: 'knowing the water',
      stuck: 'stuck, or confidently wrong',
      youFix: 'you: read, find the bug, fix by hand',
      webbShort: 'Matthew Webb',
      captainSwim: 'a captain who could swim',
      stillSwim: 'farther from shore: still learn to swim',
      trieste: 'the Trieste, 23 January 1960',
      triesteCredit: 'over the Mariana Trench, US Navy',
      piccardWalsh: 'Jacques Piccard, Don Walsh',
      insideTrieste: 'inside the Trieste, 1960',
      challengerLine: 'Challenger’s line, 8,184 m',
      deepest: '≈ 10,911 m',
      challengerDeep: 'Challenger Deep',
      theMachine: 'the machine',
      didDiving: 'did the diving',
      thePeople: 'the people',
      readGauges: 'read every gauge',
      madeDecisions: 'made every decision',
      tagline: 'Learn to swim. Then learn to sail.',
    },
  },
};

export const ar: typeof en = {
  player: {
    title: 'الفكرة في رسوم متحركة',
    play: 'تشغيل',
    pause: 'إيقاف مؤقت',
    resume: 'متابعة',
    replay: 'شاهد مرة أخرى',
    previous: 'المشهد السابق',
    next: 'المشهد التالي',
    mute: 'أوقف صوت الراوي',
    unmute: 'شغّل صوت الراوي',
    volume: 'مستوى صوت الراوي',
    rate: (rate: number) => `${rate}×`,
    rateLabel: (rate: number) => `سرعة التشغيل ${rate}×. غيّرها`,
    scene: (n: number, of: number) => `المشهد ${num(n, 'ar')} من ${num(of, 'ar')}`,
  },

  firstCommits: {
    narration: {
      copies: beats(
        'تبدأ قصة كل مبرمج بملف يعمل.',
        'ومعه يبدأ الخوف من إفساده. فتصنع نسخة منه، ثم نسخة أخرى.',
        'ثم نسخة أخيرة اسمها «actually working».',
        'بعد أسبوع، لا أحد يتذكّر أيّها النسخة الحقيقية.',
      ),
      collision: beats(
        'ثم ينضمّ إليك زميل.',
        'تفتحان الملف نفسه في اليوم نفسه، ويعدّله كلٌّ منكما.',
        'في المساء، لا تنجو إلا نسخة واحدة.',
        'أما عمل الآخر طوال تلك الساعات، فقد ضاع ببساطة.',
      ),
      central: beats(
        'لعقود، حلّت الفرق هذه المشكلة بخادم مركزي واحد يحتفظ بالسجل كله.',
        'أما بقية الأجهزة، فكانت تحمل أحدث الملفات فقط.',
        'نجح ذلك، إلى أن ينقطع الاتصال. بلا شبكة، لا سجل.',
      ),
      clones: beats(
        'في أبريل 2005، احتاج Linus Torvalds إلى أداة أفضل لتطوير Linux، فكتب Git في نحو أسبوعين.',
        'فكرته الكبرى: كل نسخة كاملة. عندما تنسخ المستودع، يأتي السجل كله معه.',
        'أما GitHub، فهو نسخة أخرى فقط، اتفق الجميع على اعتبارها الأهم.',
      ),
      hashes: beats(
        'كل commit لقطة من مشروعك، واسمه hash يُحسب من محتواه ومن الـ commit الذي قبله.',
        'الآن غيّر بايتًا واحدًا في commit قديم.',
        'يتغيّر الـ hash الخاص به، وكل hash بعده. لا أحد يستطيع إعادة كتابة الماضي من دون أن يظهر ذلك.',
      ),
      branches: beats(
        'وماذا عن الفرع؟ الفرع مجرد اسم يشير إلى commit واحد.',
        'إنشاء فرع جديد يحدث في لحظة.',
        'أنشئ commit عليه، فيتقدّم الاسم إلى الأمام، ولا يُنسخ أي شيء آخر.',
        'الفرع كله ملف صغير يحمل hash واحدًا.',
      ),
      threePlaces: beats(
        'وهذا يقودنا إلى الفكرة التي يقوم عليها هذا الفصل: قد يكون عملك في ثلاثة أماكن.',
        'أولًا، الملفات التي تعدّلها.',
        'ثم staging area، وهي المساحة التي تجهّز فيها الـ commit التالي.',
        'ثم السجل، بعد أن تنشئ commit.',
        'كلما حيّرك Git، اسأل: عن أي مكان من الثلاثة يتحدث؟ والآن، أنشئ أول commit لك.',
      ),
    },
    fig: {
      whichOne: 'أيّها النسخة الحقيقية؟',
      you: 'أنت',
      colleague: 'زميلك',
      lost: 'ضاع عمل ساعات',
      server: 'الخادم',
      history: 'السجل',
      filesOnly: 'الملفات فقط',
      offline: 'بلا شبكة: لا سجل',
      yourLaptop: 'جهازك',
      theirLaptop: 'جهاز زميلك',
      agreedCopy: 'النسخة المتفق عليها',
      oneByte: 'تغيّر بايت واحد',
      laterHashes: 'يتغيّر كل hash بعده',
      wholeBranch: 'هذا هو الفرع كله:',
      // The labels Git's own messages and every team use, kept Latin and
      // isolated, as on the warm-up's three-trees figure.
      workingTree: ltr('working tree'),
      stagingArea: ltr('staging area'),
      edited: 'معدَّل',
      staged: ltr('staged'),
      committed: 'في السجل',
    },
  },
  workstation: {
    narration: {
      buttons: beats(
        'كل برنامج استخدمته في حياتك فيه أزرار.',
        'لكن الزر لا يفعل إلا ما صُمّم من أجله.',
        'تريد إعادة تسمية 400 ملف؟ إن لم يصنع أحد هذا الزر، فستنقر 400 مرة.',
      ),
      teletype: beats(
        'سطر الأوامر أقدم من الشاشة. في عام 1969، في مختبرات Bell، كان terminal آلة كاتبة موصولة بحاسوب.',
        'كان كل حرف يُطرق على الورق، بسرعة نحو عشرة أحرف في الثانية.',
        'لذلك جاءت أسماء الأوامر قصيرة، مثل ls و cp.',
      ),
      pipe: beats(
        'ثم في عام 1973 أضاف Doug McIlroy فكرة الأنبوب، أو pipe: تصبح مخرجات برنامج مدخلات البرنامج التالي.',
        'ابحث في كل الملفات، واحتفظ بالأسطر التي فيها سعر، ثم رتّبها، ثم عُدّها.',
        'لم يبنِ أحد هذه الميزة. أنت ركّبتها من أربع أدوات صغيرة، في 15 ثانية.',
      ),
      survived: beats(
        'في عام 1984 وصل Macintosh، واختفى سطر الأوامر من حياة معظم الناس.',
        'لكنه بقي عند من يديرون الحواسيب في عملهم.',
        'فالأمر دقيق: ليس وصفًا للخطوة، بل هو الخطوة نفسها.',
      ),
      servers: beats(
        'ويمكن تكراره: ضعه في ملف، فينفّذه الحاسوب عشرة آلاف مرة، في الرابعة فجرًا، من دونك.',
        'وهناك توجد الخوادم. معظم الأجهزة التي تشغّل الإنترنت ليست لها شاشة أصلًا.',
        'والطريقة الوحيدة للتحدث إليها سطر من النص، مثل الذي أمامك.',
      ),
      real: beats(
        'الـ terminal في هذه الدورة ليس محاكاة لأوامر جاهزة: متصفحك يشغّل Linux حقيقيًا على حاسوب مُحاكى.',
        'اكتب أمرًا لم نفكّر فيه، فسيعمل، أو يفشل بالخطأ الحقيقي نفسه.',
        'ويُقيَّم عملك بحالة الجهاز، لا بما كتبته.',
        'اسلك أي طريق تريده. والآن، اكتب أول أمر لك.',
      ),
    },
    fig: {
      open: 'فتح',
      save: 'حفظ',
      print: 'طباعة',
      renameAll: 'إعادة تسمية 400 ملف',
      byHand: 'نقرة بعد نقرة',
      teletype: 'آلة كاتبة موصولة، 1969',
      computer: 'الحاسوب',
      tenASecond: 'نحو 10 أحرف في الثانية',
      fourTools: 'أربع أدوات صغيرة، 15 ثانية',
      desktop: '1984: وصل سطح المكتب',
      professionals: 'بقي عند المحترفين',
      isTheAction: 'الخطوة نفسها',
      everyNight: 'كل ليلة، من دونك',
      noScreen: 'بلا شاشة',
      realLinux: 'Linux حقيقي، في متصفحك',
      keystrokes: 'ما كتبته',
      state: 'ما يحمله الجهاز',
    },
  },
  branches: {
    narration: {
      alone: beats(
        'حتى الآن كنت تعمل وحدك. كان السجل خطًا مستقيمًا، لأن شيئًا آخر لم يكن يحدث.',
        'هذا يتغيّر الآن. من هنا فصاعدًا، تتغيّر الشيفرة بينما تغيّرها أنت.',
        'يعدّلها أشخاص لم تقابلهم قط، في مناطق زمنية لم تزرها قط.',
      ),
      peers: beats(
        'إليك ما لا يقوله أحد تقريبًا بصوت عالٍ: لا يوجد خادم في Git.',
        'مستودعك ومستودعهم نظيران: من النوع نفسه، ولا أحد منهما هو المسؤول.',
        'اسم origin مجرد لقب يحتفظ به مستودعك لعنوان مستودع آخر.',
      ),
      push: beats(
        'لذلك فالمشاركة شيء تفعله عن قصد: تنفّذ push.',
        'توجد commits الخاصة بك كاملة ودائمة قبل أن يراها أي شخص آخر.',
        'لهذا يمكنك العمل أسبوعًا في قطار بلا إشارة، من دون أن تخسر شيئًا.',
      ),
      conflict: beats(
        'ثم يغيّر شخصان الأسطر نفسها، في اليوم نفسه.',
        'كان بإمكان Git أن يخمّن أيهما على صواب. ومن حين لآخر، سيحذف تخمين صامت إصلاحًا لخطأ.',
        'لذلك يتوقف، ويعرض عليك النسختين، ويسأل الوحيد القادر على القرار: شخصًا يعرف الغرض من الشيفرة.',
      ),
      branch: beats(
        'الفرع لا يكلّف شيئًا، لذلك تعمل الفرق كلها بالطريقة نفسها.',
        'تنشئ فرعًا لتغييرك، وتعمل عليه، ثم تقترحه على الخط الرئيسي، حيث يقرؤه أحد أولًا.',
        'يبقى الخط الرئيسي جاهزًا للإطلاق، وتبقى أخطاؤك على فرعك.',
      ),
      routine: beats(
        'التعارضات أمر معتاد، والطريقة لإبقائها صغيرة أن تواجهها كثيرًا.',
        'الرهبة منها شعور عام، ولا مبرر له، ويختفي في المرة الثالثة التي تحلّ فيها تعارضًا.',
        'والآن، أرسل عملك إلى مكان يستطيع الآخرون الوصول إليه.',
      ),
    },
    fig: {
      you: 'أنت',
      colleague: 'زميلك',
      yourRepo: 'مستودعك',
      theirRepo: 'مستودعهم',
      noServer: 'لا خادم: نظيران',
      offline: 'بلا إشارة، ولا خسارة',
      sameLines: 'الأسطر نفسها',
      yourVersion: 'نسختك',
      theirVersion: 'نسختهم',
      aPerson: 'يقرّر شخص',
      main: 'الخط الرئيسي: جاهز دائمًا',
      reviewed: 'يُقرأ أولًا',
      often: 'صغيرة، وكثيرة',
    },
  },
  recovery: {
    narration: {
      senior: beats(
        'الفرق بينك وبين المهندس الخبير ليس الموهبة.',
        'يحذف الشيفرة من دون تردّد، ويجرّب إعادة الهيكلة التي على الأرجح لن تنجح.',
        'ليس أشجع منك. هو يعرف أن شيئًا تقريبًا لا يستطيع إتلاف عمل سُجّل في commit.',
      ),
      labels: beats(
        'الـ commit كائن، واسمه hash لما في داخله.',
        'الفرع مجرد لافتة تشير إلى commit. احذف الفرع، فتذهب اللافتة وحدها.',
        'يبقى الـ commit حيث كان، سليمًا تمامًا. كل ما في الأمر أن الوصول إليه بالاسم صار أصعب.',
      ),
      record: beats(
        'ويتذكّر Git أين كانت اللافتات.',
        'في كل مرة يتحرك فيها فرع، يكتب Git أين كان، وإلى أين ذهب.',
        'لذلك فالـ commit الذي فقدته قبل خمس دقائق مكتوب، باسمه، في ملف.',
      ),
      net: beats(
        'هناك حدّ واحد يجب قوله بصراحة: شبكة الأمان تبدأ عند الـ commit.',
        'التغييرات التي لم تسجّلها في commit ملفات عادية، بلا سجل ولا شبكة.',
        'لذلك فتسجيل العمل كثيرًا ليس ترتيبًا. إنه تأمين.',
      ),
      rewrite: beats(
        'ويتيح لك Git أيضًا ترتيب الماضي: دمج commits، وإعادة صياغتها، ونقل خط عمل كامل.',
        'لا أحد يريد أن يقرأ «wip» و«fix» و«actually fix» و«typo» بعد ستة أشهر.',
        'لكن تغيير commit ينشئ commit جديدًا. لذلك أعد كتابة السجل الذي لا يبني عليه أحد غيرك فقط.',
      ),
      disasters: beats(
        'ثلاث كوارث صغيرة قادمة، وستتعافى من كل واحدة منها.',
        'في النهاية، يجب أن تتوقف عن تصديق جملة «أظن أنني فقدت عملي».',
        'ابدأ بالعثور على commit اختفى.',
      ),
    },
    fig: {
      deleted: 'حُذف من دون تردّد',
      label: 'لافتة',
      object: 'الـ commit: ما زال هنا',
      whereItWas: 'أين كان',
      whereItWent: 'إلى أين ذهب',
      theRecord: 'سجل كل حركة',
      uncommitted: 'بلا commit: بلا شبكة',
      committed: 'في commit: بأمان',
      yours: 'لك وحدك: رتّبه',
      shared: 'مشترك: اتركه',
      found: 'وُجد',
    },
  },

  quality: {
    narration: {
      question: beats(
        'كل وظيفة ستسألك السؤال نفسه: كيف تعرف أنه يعمل؟',
        'عبارة «اختبرته يدويًا» تعني أنك فحصت شيئًا واحدًا، مرة واحدة، في يوم واحد.',
        'ولا تقول شيئًا عن الأربعين موضعًا الأخرى التي تستخدم هذه الشيفرة، ولا عن شهر مارس القادم.',
      ),
      claim: beats(
        'الاختبار برنامج صغير يقدّم ادعاءً عن برنامجك، ويفشل بصوت عالٍ عندما يتوقف الادعاء عن أن يكون صحيحًا.',
        'أضف العنصر نفسه إلى قائمة الأمنيات مرتين. الادعاء: يظهر مرة واحدة.',
        'الادعاء يبقى بعدك. يعمل إلى الأبد، ويكشف كل من يكسره، وكثيرًا ما تكون أنت.',
      ),
      cheap: beats(
        'يعمل في ثانية، لذلك يعمل مع كل تغيير.',
        'الفحص الذي يستغرق من شخص عشر دقائق يُجرى قبل الإصدارات، ثم قبل الإصدارات الكبيرة، ثم لا يُجرى أبدًا.',
        'والاختبار يخبرك بما يُفترض أن تفعله الشيفرة، لا بما تفعله مصادفةً فقط.',
      ),
      trace: beats(
        'عندما يتعطل شيء، يترك البرنامج stack trace.',
        'يبدو كضجيج. لكنه قائمة دقيقة بما كان البرنامج يفعله، من أعمق استدعاء إلى أبعده.',
        'شيفرتك في مكان ما في الوسط، وهناك يكون الجواب في أغلب الأحيان.',
      ),
      method: beats(
        'ثم الطريقة. أعد إنتاج الخطأ، لتستطيع إظهاره متى شئت.',
        'ضيّق مكانه بالأدلة، وافهم سبب حدوثه، قبل أن تغيّر أي شيء.',
        'أصلحه، ثم أثبت ذلك: اختبار يفشل قبل إصلاحك، وينجح بعده.',
      ),
      proof: beats(
        'الخطأ الذي يُصلَح بلا اختبار يعود، وغالبًا أسوأ، وغالبًا إلى شخص آخر.',
        'في نهاية هذا الفصل، سيكون لديك جواب حقيقي عن سؤال: كيف تعرف؟',
        'ابدأ بمجموعة اختبارات فاشلة بالفعل.',
      ),
    },
    fig: {
      byHand: 'فحص يدوي: مرة واحدة',
      callers: '40 موضعًا آخر',
      claim: 'الادعاء',
      passes: 'ينجح',
      fails: 'يفشل بصوت عالٍ',
      oneSecond: 'ثانية واحدة، مع كل تغيير',
      tenMinutes: '10 دقائق: ثم أبدًا',
      innermost: 'الأعمق',
      outermost: 'الأبعد',
      yourCode: 'شيفرتك',
      reproduce: 'أعد إنتاجه',
      narrow: 'ضيّقه',
      understand: 'افهمه',
      prove: 'أصلح وأثبت',
    },
  },
  teamGit: {
    narration: {
      reader: beats(
        'حتى الآن كان الـ commit نقطة حفظ، لتستطيع العودة إليها.',
        'وهذا هو النصف الأكبر: السجل هو الأثر الدائم الوحيد لسبب كون النظام على ما هو عليه.',
        'كل commit رسالة إلى شخص غريب: زميل في الثالثة فجرًا، أو أنت بعد ثمانية عشر شهرًا.',
      ),
      message: beats(
        'تبدأ الرسالة الجيدة بسطر قصير واحد، كأنه يكمل جملة: هذا الـ commit سوف.',
        'ثم، إن احتاج التغيير، بضعة أسطر عن السبب، لا عن المحتوى.',
        'الفرق يقول بالفعل ما الذي تغيّر. أنت وحدك تعرف السبب، واليوم فقط.',
      ),
      onecommit: beats(
        'بعض الأدوات لا معنى لها إلا في فريق.',
        'يوجد إصلاح على فرع واحد، والإنتاج يحتاج هذا الإصلاح بالضبط، لا الـ commits الستة حوله.',
        'يستطيع Git نسخ تغيير ذلك الـ commit وحده إلى خط آخر، في commit جديد.',
      ),
      search: beats(
        'أو كان شيء يعمل قبل ثلاثة أسابيع وتعطّل الآن، وبينهما مئتا commit.',
        'يفتح Git الـ commit الذي في المنتصف، ويسألك هل هو سليم أم معطوب، ويقسم المشتبه بهم إلى النصف في كل مرة.',
        'تصبح المئتا commit نحو ثمانية أسئلة.',
      ),
      coherent: beats(
        'هذا ينجح فقط لأن كل commit حالة كاملة وعاملة من المشروع.',
        'الـ commits الصغيرة المرتبة يمكن البحث فيها، والتراجع عنها، ونسخها، ومراجعتها واحدًا واحدًا.',
        'أما commit واحد ضخم، فإما أن يؤخذ كله أو يُترك كله.',
      ),
      ticket: beats(
        'المستوى الأخير تذكرة كاملة: فرع، وتغيير، واختبار، وترتيب، ومشاركة.',
        'ليست الأوامر، بل الشكل. وهذا ما توظّف الشركات الناس من أجله فعلًا.',
        'بعد هذا الفصل، لن تعود الـ commits الخاصة بك لك وحدك. ابدأ بنقل commit واحد إلى حيث يلزم.',
      ),
    },
    fig: {
      why: 'السبب، لا المحتوى',
      what: 'المحتوى: في الفرق',
      stranger: 'غريب، في الثالثة فجرًا',
      theFix: 'الإصلاح',
      copied: 'نُسخ في commit جديد',
      good: 'سليم',
      bad: 'معطوب',
      questions: 'نحو 8 أسئلة',
      small: 'صغيرة وكاملة',
      huge: 'commit واحد ضخم',
      branch: 'فرع',
      change: 'تغيير',
      test: 'اختبار',
      tidy: 'ترتيب',
      share: 'مشاركة',
    },
  },
  codeReview: {
    narration: {
      gate: beats(
        'في كل شركة تستحق العمل فيها، توجد قاعدة: لا تستطيع إدخال شيفرتك إلى الخط الرئيسي وحدك.',
        'يجب أن يقرأها شخص آخر ويوافق، وهذا ينطبق على الجميع، حتى مهندس خلفه عشرون عامًا.',
        'ليس هذا انعدام ثقة. إنه يبقى في كل مكان لأنه ينجح.',
      ),
      history: beats(
        'في عام 1976 نشر Michael Fagan في IBM طريقة الفحص: اقرأ العمل سطرًا سطرًا، قبل أن يتقدّم.',
        'المشكلة التي تُكتشف مبكرًا رخيصة: عشر دقائق في المراجعة، أو حادثة وإصلاح عاجل وثقة عميل بعد الإطلاق.',
        'وفي عام 2008 جعل GitHub الفكرة نفسها غير متزامنة: الـ pull request.',
      ),
      purpose: beats(
        'اكتشاف الأخطاء ضمن القائمة، لكنه ليس في أولها.',
        'المراجعة تنشر المعرفة، فلا يبقى شخص واحد هو الوحيد الذي يفهم شيفرة الفوترة.',
        'وتضع المعيار، وتجعل التغيير تغيير الفريق، لا تغييرك وحدك.',
      ),
      reviewed: beats(
        'لتُراجَع جيدًا، اجعل التغيير صغيرًا، لأن انتباه القارئ ينفد.',
        'اشرح السبب في الوصف، ليراجعوا المشكلة لا الشيفرة وحدها.',
        'وأجب عن التعليقات بفضول. التعليق سؤال عن الشيفرة، لا حكم عليك.',
      ),
      reviewing: beats(
        'المراجعة مهارة أصعب. اقرأ للقصد قبل الأسلوب.',
        'قل هل تقصد أن هذا معطوب، أم أنه اقتراح، أم مجرد ذوقك.',
        'ووافق على الأشياء. أنفع جملة في المراجعة: قد يفوتني شيء، لكن.',
      ),
      chapter: beats(
        'أربعة مستويات، في الاتجاهين: أن تجيب عن مراجعة، وأن تقدّم مراجعة.',
        'ثم أن تشكّل تغييرًا يمكن مراجعته، وأن تلحق بفرع تأخّر عن الخط الرئيسي.',
        'هذا الفصل يجعلك شخصًا يستطيع الفريق العمل معه. ابدأ بالإجابة عن مراجعة.',
      ),
    },
    fig: {
      you: 'أنت',
      reviewer: 'مراجِع',
      everyone: 'الجميع، في كل مستوى',
      inReview: 'في المراجعة: دقائق',
      afterRelease: 'بعد الإطلاق: حادثة',
      knowledge: 'شخصان يعرفانه',
      small: 'صغير',
      huge: '1,000 سطر',
      whyText: 'السبب',
      broken: 'معطوب',
      suggestion: 'اقتراح',
      taste: 'ذوق',
      approved: 'تمت الموافقة',
    },
  },
  codebase: {
    narration: {
      blank: beats(
        'كل تمرين أدّيته بدأ من ملف فارغ.',
        'لا يكاد يوجد عمل احترافي كذلك. تنضم إلى شيفرة عمرها عشر سنوات أو عشرون، كتبها أشخاص غادروا.',
        'ستقضي في قراءة الشيفرة أكثر بكثير مما تقضيه في كتابتها. إنها مهارة مستقلة، وهي صلب العمل.',
      ),
      fence: beats(
        'أول ردّ فعل على شيفرة غريبة هو الاستخفاف: من كتب هذا؟',
        'وفي الغالب أنت تنظر إلى تاريخ: موعد نهائي، أو حاجة عميل واحد، أو خلل في مكتبة عام 2019.',
        'والقاعدة القديمة هي سياج Chesterton: لا تهدم سياجًا حتى تعرف لماذا وُضع.',
      ),
      trail: beats(
        'يستطيع Git أن يخبرك، لأي سطر، أي commit أدخله، ومن كتبه، ومتى، ولماذا.',
        'من السطر، إلى الـ commit، إلى الرسالة، إلى التذكرة.',
        'وكثيرًا ما تشرح عشر كلمات كل شيء. تتوقف عن تخمين القصد وتبدأ في البحث عنه.',
      ),
      scale: beats(
        'إعادة تسمية شيء في كل مكان ليست بحثًا واستبدالًا. عليك أن تعرف بالضبط ما لمسته.',
        'الشيفرة الميتة ليست مجانية. والخوف: ماذا لو احتاجها شيء؟ إنها محفوظة في commit. يمكن استعادتها. احذفها.',
        'وحين تؤدي وحدة واحدة أربع مهام، قسّمها، كي لا يخاطر تغيير إحداها بالثلاث الأخرى.',
      ),
      separate: beats(
        'الثلاثة تغييرات بلا سلوك جديد. لا شيء يراه المستخدم يختلف.',
        'وهذا يجعل مراجعتها سهلة، ولهذا تُفصل عن التغييرات التي تغيّر السلوك.',
        'الـ commit الذي يعيد تسمية أربعين شيئًا ويصلح خطأً معًا لا يستطيع أحد مراجعته كما ينبغي.',
      ),
      chapter: beats(
        'أربعة مستويات، كلها داخل شيفرة لم تكتبها: تتبّع سطر إلى سببه، وإعادة تسمية عبر مشروع كامل.',
        'ثم حذف شيفرة ميتة بثقة، وتقسيم وحدة كبرت أكثر من اللازم.',
        'في النهاية، يجب أن تبدو لك الشيفرة الغريبة أقل شبهًا بجدار، وأكثر شبهًا بمكان له سجلات.',
      ),
    },
    fig: {
      years: '20 سنة',
      reading: 'قراءة',
      writing: 'كتابة',
      why: 'لماذا؟',
      line: 'السطر',
      commit: 'الـ commit',
      message: 'الرسالة',
      ticket: 'التذكرة',
      rename: 'إعادة تسمية',
      dead: 'شيفرة ميتة',
      split: 'تقسيم',
      noBehaviour: 'بلا سلوك جديد',
      tidy: 'ترتيب',
      fix: 'إصلاح',
    },
  },
  releases: {
    narration: {
      line: beats(
        'كل ما سبق حدث داخل مستودع، وعدد من يمكن أن تؤذيهم كان واحدًا.',
        'الإصدار يقول: هذه الحالة بعينها من الشيفرة هي التي يستخدمها أناس حقيقيون الآن.',
        'ومن تلك اللحظة، يصبح الخطأ ظهيرةَ شخصٍ ما. والنشر سريع، لكن التراجع عنه ليس كذلك.',
      ),
      tag: beats(
        'كل إصدار يحصل على tag: علامة دائمة على commit واحد بعينه.',
        'الإصدار 2.4.1 يعني حالة واحدة من الشيفرة إلى الأبد، لا ما كان تقريبًا على main ذلك الأسبوع.',
        'حين يبلّغ عميل عن خطأ، أول سؤال: أي إصدار؟ ويجب أن يكون الجواب شيئًا تستطيع تشغيله.',
      ),
      semver: beats(
        'ثلاثة أرقام، وكل رقم وعدٌ لمن يعتمد عليك.',
        'الأخير يعني أصلحت شيئًا. والأوسط يعني أضفت شيئًا، واستخدامك الحالي ما زال يعمل.',
        'والأول يعني كسرت شيئًا: اقرأ الملاحظات قبل الترقية.',
      ),
      notes: beats(
        'وبجانب الـ tag يأتي سجل التغييرات، مكتوبًا للناس لا للمهندسين.',
        'عبارة «إعادة تنظيم معالج الطلبات» لا تقول للمستخدم شيئًا.',
        '«الطلبات المقدّمة في الدقيقة الأخيرة من التخفيضات لم تعد تُرفض». إنه التغيير نفسه، والآن يعرفون هل يمسّهم.',
      ),
      hotfix: beats(
        'شيء معطوب في الإنتاج، والفرع main تقدّم بأعمال غير مكتملة لآخرين.',
        'فتعود إلى النسخة المُصدرة، وتضع أصغر إصلاح هناك، وتُصدره.',
        'ثم تعيد الإصلاح إلى main، وإلا ألغاه الإصدار التالي بصمت.',
      ),
      rollback: beats(
        'الغريزة الأشجع هي التراجع: أعد آخر نسخة سليمة أولًا، وابحث عن السبب بعد ذلك.',
        'إيقاف الضرر ليس هو إصلاح المشكلة، وفعلهما بهذا الترتيب علامة خبرة.',
        'وهذا ممكن فقط لأن أحدًا وضع tag على آخر نسخة سليمة.',
      ),
      chapter: beats(
        'أربعة مستويات: وسم إصدار، وكتابة ملاحظاته بكلمات يستخدمها الناس.',
        'ثم إصلاح نسخة مُصدرة بينما تقدّم main، والتراجع عن إصدار سيئ.',
        'هنا يتوقف العمل عن كونه تمرينًا. ابدأ بوسم إصدار.',
      ),
    },
    fig: {
      repository: 'المستودع',
      users: 'مستخدمون حقيقيون',
      patch: 'إصلاح',
      minor: 'إضافة',
      major: 'كسر',
      forUsers: 'للمستخدمين',
      released: 'المُصدرة',
      theFix: 'الإصلاح',
      lastGood: 'آخر سليمة',
      bad: 'سيئة',
    },
  },
  production: {
    narration: {
      night: beats(
        'الساعة الثانية فجرًا. شيء كان يعمل قد توقّف، وأناس يتضرّرون الآن.',
        'المهندسون الخبراء يتعاملون مع هذا جيدًا، لا لأنهم يعرفون النظام أكثر غالبًا، بل لأن لديهم إجراءً.',
        'وهو مصمَّم لحالتك في تلك الساعة بالضبط: خائف، متعب، ومستعد للتخمين.',
      ),
      bleeding: beats(
        'إعادة الخدمة وإصلاح السبب عملان مختلفان، ويحدثان بهذا الترتيب.',
        'إن كان التراجع إلى إصدار الأمس يعيد العمل، فتراجع، حتى لو لم تعرف السبب بعد.',
        'وحين يتوقف النزيف، تستطيع التحقيق بهدوء، بالأدلة، ومن دون ساعة تلاحقك.',
      ),
      logs: beats(
        'ثم الأدلة لا النظريات. النظام العامل يترك أثرًا، وقراءته تعني التضييق.',
        'من «الموقع متوقف»، إلى «هذه الواجهة تفشل بعد هذا النشر»، إلى «هذا الاستدعاء يرمي خطأً حين يغيب حقل».',
        'يجب أن تستند كل خطوة إلى شيء رأيته، لا شيء تشكّ فيه.',
      ),
      reproduce: beats(
        'أثمن لحظة في أي حادثة هي حين تستطيع إحداث العطل متى شئت.',
        'قبلها لديك قصة. وبعدها لديك شيء تختبره.',
        'ومن دونها تصلح أشياء لم تكن السبب قط، وتعود المشكلة بعد ساعة.',
      ),
      postmortem: beats(
        'بعد ذلك، تكتب الفرق الجيدة ما حدث: ماذا، ومتى، ولماذا، وما الذي سيتغيّر.',
        'وتكتبه بلا لوم: الجميع تصرّفوا بشكل معقول، فلماذا جعل النظام ذلك خطرًا؟',
        '«أحدهم نشر دون فحص» طريق مسدود. «كان النشر دون فحص ممكنًا» نتيجة، ولها إصلاح.',
      ),
      chapter: beats(
        'أربعة مستويات، تتبع حادثة واحدة من بدايتها إلى نهايتها.',
        'قراءة السجلات، وإعادة إنتاج العطل، وإيقاف النزيف، وكتابة التقرير.',
        'المقصود أن تشبه ليلة سيئة في عمل حقيقي، كي لا تكون المرة الثانية هي الأولى. ابدأ بالسجلات.',
      ),
    },
    fig: {
      broken: 'متوقف',
      restore: 'إعادة الخدمة',
      diagnose: 'التشخيص',
      onDemand: 'متى شئت',
      timeline: 'التسلسل الزمني',
      why: 'السبب',
      change: 'ما سيتغيّر',
    },
  },
  pipeline: {
    narration: {
      discipline: beats(
        'اسأل فريقًا كيف يحافظ على الجودة، فتسمع قائمة: شغّل الاختبارات، افحص التنسيق، لا تضف ذلك الملف.',
        'لا شيء منها يصمد أمام ظهيرة خميس قبل عطلة. الناس متعبون، أو جدد، أو مستعجلون.',
        'لذا إن كانت القاعدة مهمة، فيجب أن تفرضها آلة. لا تعليقًا على أحد، بل حسابًا بسيطًا.',
      ),
      history: beats(
        'كانت الفرق تعمل منفصلة لأسابيع، ثم تدمج كل شيء قرب الموعد النهائي.',
        'فتظهر كل حالات عدم التوافق دفعة واحدة. كان لذلك اسم، «جحيم الدمج»، وقد يلتهم خُمس الجدول.',
        'وكان الحل هو الدمج باستمرار، مع فحص آلي في كل مرة. تعارض ساعتين، بدل تعارض شهرين.',
      ),
      command: beats(
        'الطبقة الأولى أمر واحد: test أو check أو build، بدل فقرة في ملف README خاطئة منذ مارس.',
        'إنه الأساس لكل ما يأتي بعده.',
        'وهو ألطف ما يمكنك فعله للشخص التالي الذي ينضم.',
      ),
      hook: beats(
        'الطبقة الثانية hook. يشغّل Git ذلك الأمر عنك، قبل إنشاء الـ commit.',
        'الآن يحدث الفحص دون أن يختار أحد تشغيله.',
        'ثم يشغّله التكامل المستمر على جهاز نظيف، لكل تغيير، وللجميع. «يعمل على جهازي» صحيح غالبًا، وهذه هي المشكلة.',
      ),
      flaky: beats(
        'الاختبار المتذبذب ينجح أحيانًا ويفشل أحيانًا، وهو أسوأ من الفشل.',
        'فهو يعلّم الفريق أن البناء الأحمر قد لا يعني شيئًا، فيعيدون تشغيله فحسب.',
        'فحص موثوق يلتقط تسعين بالمئة أثمن من فحص غير موثوق يلتقط كل شيء. الثقة هي ما يقوم عليه كل هذا.',
      ),
      chapter: beats(
        'أربعة مستويات: أمر واحد يستطيع أي شخص تشغيله، و hook يشغّله عنك.',
        'ثم جعل عطل عشوائي حتميًا، وخط أنابيب يفحص كل تغيير، إلى الأبد.',
        'الأتمتة طريقة لمساعدة أناس ليسوا في الغرفة. ابدأ بالأمر الواحد.',
      ),
    },
    fig: {
      tests: 'شغّل الاختبارات',
      format: 'افحص التنسيق',
      notThatFile: 'ليس ذلك الملف',
      weeks: 'أسابيع منفصلة',
      daily: 'كل يوم',
      beforeCommit: 'قبل الـ commit',
      yourMachine: 'جهازك',
      cleanMachine: 'جهاز نظيف',
      rerun: 'إعادة التشغيل',
    },
  },
  data: {
    narration: {
      net: beats(
        'كان لكل فصل حتى الآن شبكة أمان. التزم مبكرًا، واحذف بجرأة، لأنك تستطيع العودة دائمًا.',
        'كان ذلك صحيحًا لأن Git يحتفظ بنسخ من الشيفرة. لكنه لا يحتفظ بنسخ من بياناتك.',
        'غيّر قاعدة بيانات الإنتاج، ولن تجد commit سابقًا تعود إليه. فقط نسخة احتياطية، إن كانت تعمل.',
      ),
      twice: beats(
        'البيانات أصعب لثلاثة أسباب. أولًا، تشغيل النشر مرتين لا يضر. أما إضافة عشرة بالمئة إلى كل سعر مرتين فيضر.',
        'السكربتات تنتهي مهلتها ويُعاد تشغيلها، وتنطلق إعادات المحاولة. لذا يجب أن يكون كل تغيير آمنًا عند تشغيله مرة أخرى.',
        'ثانيًا، لفترة ما، تعمل الخوادم القديمة والجديدة معًا على قاعدة بيانات واحدة.',
      ),
      stages: beats(
        'لذا يُقسَّم التغيير على مراحل: أولًا شكل يتعايش معه الإصداران.',
        'ثم انقل البيانات، وانشر الشيفرة الجديدة.',
        'وبعدها فقط أزل الشكل القديم. ثلاث عمليات نشر لتنفيذ ما يبدو عملية واحدة بأمان.',
      ),
      batches: beats(
        'ثالثًا، حجمها كبير بما يكفي ليؤذي. ما هو فوري على مئة صف قد يقفل جدولًا عشرين دقيقة على مئة مليون.',
        'لذا تعمل على دفعات، وتستطيع التوقف والاستئناف.',
        'ولا يعود هذا ترفًا، بل الطريقة الوحيدة التي تنجح.',
      ),
      habits: beats(
        'اعرف بالضبط كيف ستتراجع قبل أن تنفّذ. «الاستعادة من النسخة الاحتياطية» أمل لا خطة.',
        'لا تجعل خطوة الحذف أولى الخطوات. انسخ، وتحقّق، ثم احذف بعد أيام.',
        'عُدّ قبل وبعد. أردت أن تمسّ أربعة آلاف صف، فمسست تسعين ألفًا.',
      ),
      chapter: beats(
        'ثلاثة مستويات، وهي التي تسوء في الحياة الحقيقية.',
        'إعادة تشكيل البيانات تحت شيفرة تعمل، وملء جدول كبير على دفعات، وتغيير كان سيدمّر البيانات بصمت.',
        'في كل مكان آخر كانت النصيحة أن تجرّب. هنا العكس: حين لا يمكن التراجع، تمهّل.',
      ),
    },
    fig: {
      code: 'الشيفرة',
      data: 'البيانات',
      backup: 'نسخة احتياطية؟',
      runTwice: 'تشغيل مرتين',
      oldCode: 'الشيفرة القديمة',
      newCode: 'الشيفرة الجديدة',
      expand: 'أضف',
      migrate: 'انقل',
      contract: 'أزل',
      rows: '100,000,000 صف',
      undo: 'التراجع',
      copy: 'انسخ',
      verify: 'تحقّق',
      remove: 'احذف',
      expected: 'المتوقع',
      touched: 'ما تغيّر',
    },
  },
  design: {
    narration: {
      unknown: beats(
        'حتى الآن كان لمعظم المشكلات جواب صحيح: الاختبار ينجح أو لا ينجح.',
        'بعض التغييرات مختلفة. هل ستبيع صفحة الدفع هذه أكثر؟ هل سيكون هذا أسرع مع حركة حقيقية؟ لا أحد يعرف.',
        'المهارة ليست كتابة التغيير. بل ترتيب الأمور بحيث يكون الخطأ رخيصًا.',
      ),
      flag: beats(
        'نشر الشيفرة وتفعيل الميزة لا يجب أن يكونا اللحظة نفسها.',
        'ضع السلوك الجديد خلف flag، مُطفأً افتراضيًا. يمكن دمجه ونشره وهو لا يفعل شيئًا على الإطلاق.',
        'ثم فعّله لنفسك، ثم للفريق، ثم لواحد بالمئة من المستخدمين، ثم للجميع.',
      ),
      off: beats(
        'وإن ساء شيء في أي لحظة، تطفئه. ثوانٍ، بلا نشر ولا حادثة.',
        'لكن كل flag نسخة ثانية من نظامك يجب أن يفكّر فيها أحد.',
        'لذا حين تُحسم المسألة، يُزال الـ flag.',
      ),
      contract: beats(
        'في اللحظة التي يعتمد فيها شيء على شيفرتك، يصبح شكل تلك الواجهة وعدًا، سواء تذكّرت أنك قطعته أم لا.',
        'غيّر اسم حقل، فتنجح اختباراتك، ويتعطّل شخص يستخدم تطبيقًا من عام 2023.',
        'لذا تضيف بدل أن تعدّل، وتُبقي المسار القديم يعمل بجانب الجديد، وتمنح الناس طريقًا للانتقال.',
      ),
      measure: beats(
        'المهندسون سيئون بثقة في تخمين أين يقضي البرنامج وقته.',
        'نادرًا ما تكون الخوارزمية الذكية. بل استعلام داخل حلقة، أو قراءة ملف لم يلحظها أحد.',
        'لذا قِس أولًا، واعرف ما معنى «سريع بما يكفي» قبل أن تبدأ، وإلا فلن تتوقف أبدًا.',
      ),
      chapter: beats(
        'أربعة مستويات، كل منها تغيير خطر أصبح آمنًا بالبنية، لا بالحذر.',
        'flag، وواجهة تتغيّر دون أن تكسر أحدًا، وشيء أصبح أسرع بقياس، ومعرفة أي التغييرات تحتاج إلى هذا.',
        'إنه أول فصل عن الحكم أكثر من التقنية. ابدأ بالـ flag.',
      ),
    },
    fig: {
      pass: 'نجح',
      fail: 'فشل',
      unknown: '؟',
      deployed: 'منشور',
      on: 'مفعّل',
      off: 'مُطفأ',
      you: 'أنت',
      team: 'الفريق',
      everyone: 'الجميع',
      yourTests: 'اختباراتك',
      theirApp: 'تطبيقهم',
      guess: 'تخمين',
      measured: 'قياس',
      enough: 'سريع بما يكفي',
      algorithm: 'الخوارزمية',
      queryLoop: 'استعلام داخل حلقة',
      fileRead: 'قراءة ملف',
    },
  },
  ownership: {
    narration: {
      scope: beats(
        'السلّم في هذه المهنة لا يقيس المهارة حقًا. بل يقيس ما أنت مسؤول عنه.',
        'المهندس المبتدئ مسؤول عن تذاكره. والمهندس الخبير مسؤول عن نظام، في عطلة نهاية الأسبوع، حتى الأجزاء التي لم يخترها.',
        'الملكية ليست مكافأة. إنها تذهب إلى من يصلحون المعطوب الذي لا صاحب له، بدل أن يلاحظوا أنه ليس لهم.',
      ),
      dependency: beats(
        'إضافة مكتبة تبدو مجانية. سطر واحد، وتمضي في طريقك بعد الظهر.',
        'لكنك ترث أخطاءها، وثغراتها الأمنية، وجدول إصداراتها، واعتمادياتها هي، ربما مئتين منها.',
        'لذا السؤال ليس: هل تعمل؟ بل: هل تستحق أن أملكها، محسوبًا على سنوات لا على ساعات.',
      ),
      runbook: beats(
        'الـ runbook يقول ما هذا الشيء، وكيف يتعطّل، وما الذي تفحصه أولًا، وكيف تعيد تشغيله، ومن توقظ.',
        'إنه مكتوب لشخص كفء، تحت ضغط، في الثالثة فجرًا، لم يلمسه قط. قصير، مرتّب، بأوامر حقيقية.',
        'واختباره: هل يستطيع شخص آخر اتباعه وحده؟ وهو طريقتك لتكفّ عن أن تكون الوحيد القادر على إبقائه حيًّا.',
      ),
      oncall: beats(
        'تنتهي الدورة بليلة مناوبة. النظام يعمل سواء راقبته أم لا، وإن تعطّل، تُوقَظ أنت.',
        'كل شيء يصل دفعة واحدة: السجلات، والتراجع، والسبب، والإصلاح، والتقرير.',
        'كل عادة في هذه الدورة كانت رسالة مرسلة إلى الأمام، إلى من يحمل جهاز النداء. وكثيرًا ما يكون ذلك أنت.',
      ),
      chapter: beats(
        'ثلاثة مستويات تطلب الحكم لا المعرفة: أن ترفض اعتمادية، وتعرف لماذا.',
        'وأن تكتب الوثيقة التي تُبقي نظامك حيًّا من دونك، وليلة مناوبة واحدة.',
        'لن يجعلك هذا خبيرًا. لكنك ستكون قد فعلت، ولو مرة، كل ما يتكوّن منه العمل. وستعرفه حين تراه.',
      ),
    },
    fig: {
      tickets: 'تذاكر',
      system: 'نظام',
      oneLine: 'سطر واحد',
      whatFails: 'كيف يتعطّل',
      checkFirst: 'افحص أولًا',
      restart: 'أعد التشغيل',
      wake: 'من توقظ',
    },
  },
  platform: {
    narration: {
      guest: beats(
        'طوال خمسين مستوى، كان المستودع لك. وهذا ينتهي في الأسبوع الأول من عمل حقيقي.',
        'ما يعطّلك في العمل نادرًا ما يكون في شيفرتك. إنه خطأ في مكتبة يملكها فريق على بعد ثلاثة فرق.',
        'تستطيع قراءتها كلها، وتعديل نسختك، ولا شيء من ذلك يوصل إصلاحك إلى حيث يعمل.',
      ),
      history: beats(
        'في عام 2005 كتب Linus Torvalds أداة Git، وكانت كل نسخة مستودعًا كاملًا. ولم يعرف Git قط من أنت.',
        'لذا كان على شيء يقف أمام Git أن يجيب عن كل سؤال عمّن يحق له تغيير ماذا.',
        'وفي عام 2008 أجاب GitHub بشيئين: زر الـ fork وصفحة الـ pull request.',
      ),
      fork: beats(
        'الـ fork نسخة من الصلاحية لا من الشيفرة. إنه مستودع على الخادم يحق لك الكتابة فيه.',
        'والـ pull request رسالة بين مستودعين: هذا فرع، وهذا سبب رغبتك فيه.',
        'فتحه لا يحرّك شيئًا. يقرّر شخص لديه صلاحية على الطرف الآخر، ويستطيع أن يرفض.',
      ),
      protect: beats(
        'ثم تعلّمت الفرق أن القاعدة المكتوبة في wiki ليست ضابطًا.',
        'الفرع المحمي برنامج على الخادم يعمل قبل أن يتحرّك أي شيء. حين يُرفض الـ push، لا يكون قد كُتب شيء.',
        'والفحص الإلزامي يجعل نتيجة الاختبار ملزمة للجميع، بمن فيهم من كتب الفحص.',
      ),
      you: beats(
        'أرسل إلى فريق آخر تغييرًا نظيفًا مختبرًا في commit واحد مع أربع جمل من السياق، ويصل الإصلاح هذا الأسبوع.',
        'ومن الجهة الأخرى، أكثر من قد يدفع شيئًا غير مراجَع إلى main الخاص بك هو أنت، وأنت مستعجل.',
        'الحدّ الذي يستثني صاحبه يفشل تمامًا حين يُحتاج إليه.',
      ),
      chapter: beats(
        'مستويان، من كل جانب من الحدّ مستوى.',
        'أولًا، أصلح خطأً في مكتبة لا تستطيع الـ push إليها. ثم احمِ فرعًا، وشاهده يوقف push، حتى لو كان منك.',
        'من يحق له تغيير هذا، وماذا يحدث حين يحاول من لا يحق له؟ ابدأ بالـ fork.',
      ),
    },
    fig: {
      yours: 'لك',
      theirs: 'لهم',
      noUsers: 'بلا مستخدمين',
      fork: 'الـ fork الخاص بك',
      upstream: 'مستودعهم',
      refused: 'مرفوض',
      check: 'فحص',
      you: 'أنت',
    },
  },
  program: {
    narration: {
      lie: beats(
        'شيء ما خاطئ في الإنتاج. الشيفرة صحيحة، والفرع صحيح، والاختبارات تنجح.',
        'كل ملف تعرف كيف تقرؤه يقول إن النظام سليم، وهو ليس كذلك.',
        'الشيء الذي يعمل ليس الشيء الذي تقرؤه.',
      ),
      copy: beats(
        'البرنامج العامل نسخة، أُخذت حين بدأ، وتعيش في الذاكرة.',
        'لا اسم له، وليس تحت التحكم بالإصدارات، ولا يترك تاريخًا.',
        'غيّر الملف، أو احذفه حتى، وتستمر العملية تمامًا كما كانت.',
      ),
      fork: beats(
        'منذ عام 1969، أنشأ Unix العملية على خطوتين: انسخ عملية قائمة، ثم استبدل البرنامج داخل النسخة.',
        'بدا ذلك هدرًا. لكن في الفجوة بين الخطوتين، تستطيع تغيير المكان الذي تقف فيه العملية الجديدة، وإلى أين يذهب خرجها.',
        'كل أنبوب، وكل إعادة توجيه، وكل مهمة في الخلفية كتبتها تعيش في تلك الفجوة.',
      ),
      signals: beats(
        'تُخاطَب العملية بالإشارات: رقم، بلا رسالة مرفقة.',
        'كل إشارة تقريبًا طلب. يستطيع البرنامج أن يجيبه، أو يؤجّله، أو يتجاهله.',
        'اثنتان لا يمكن التقاطهما. KILL و STOP تتولاهما النواة وحدها، ولا يُخبَر البرنامج أبدًا.',
      ),
      parts: beats(
        'العملية أربعة أشياء: البرنامج، وذاكرتها الخاصة، واتصالاتها، ورقم.',
        'ذاكرتها تحمل كل ما لم يُكتب بعد. ولهذا فإيقاف العملية قرار، لا إجراء شكلي.',
        'والرقم مجرد فهرس. حين تنتهي العملية، تعطيه النواة لشيء آخر.',
      ),
      chapter: beats(
        'يأخذك هذا الفصل خارج Git لأول مرة، إلى آلة.',
        'شيء يحتسب أسعارًا خاطئة ولا يستطيع المستودع تفسيره. ثم مهمتان يجب إخراجهما من جهاز قبل إعادة بنائه.',
        'إحداهما تحمل طلبات غير موجودة في أي ملف. والأخرى طُلب منها التوقف، وقرّرت ألا تفعل. ابدأ بما يعمل.',
      ),
    },
    fig: {
      reading: 'ما تقرؤه',
      running: 'ما يعمل',
      memory: 'الذاكرة',
      copyStep: 'انسخ',
      replace: 'استبدل',
      request: 'طلب',
      kernel: 'النواة',
      program: 'البرنامج',
      ownMemory: 'الذاكرة',
      connections: 'الاتصالات',
      number: 'رقم',
    },
  },
  network: {
    narration: {
      elsewhere: beats(
        'حتى الآن، كان كل ما تصلحه أمامك: المستودع، والعملية، وحتى الملف الذي بلا اسم.',
        'الاستدعاء عبر الشبكة هو أول مرة يكون فيها الشيء في مكان آخر، تحت سيطرة شخص آخر.',
        'لا تستطيع قراءة سجلاته، ولا شيفرته غالبًا. ما تستطيعه هو أن تسأله بعناية، وتقرأ الجواب كما ينبغي.',
      ),
      library: beats(
        'معظم المهندسين سيئون في هذا، لا لأنه صعب.',
        'بل لأن مكتبة عميل تقف في الطريق دائمًا تقريبًا، فتحوّل المحادثة كلها إلى سطر واحد ينجح أو يرمي خطأً.',
        'وحين يرمي خطأً، يكون تتبّع المكدس عن المكتبة.',
      ),
      history: beats(
        'صُمّم HTTP عام 1989 في CERN، ليتشارك الفيزيائيون الوثائق. كانت نسخته الأولى نحو عشرين سطرًا.',
        'وفي عام 1991 جاء القرار الذي جعله يدوم: البروتوكول نص، ويستطيع أي أحد إضافة header.',
        'لذا تستطيع قراءته، وكتابته بيدك. وهكذا يصلحه الناس فعلًا.',
      ),
      stateless: beats(
        'الخادم لا يتذكرك بين طلب وآخر.',
        'من أنت، وماذا تقبل، وماذا ترسل، وهل هذه إعادة محاولة: كل ذلك يسافر في الـ headers، في كل مرة.',
        'ولهذا يسوء الكثير هناك، وقليلون من ينظرون.',
      ),
      anatomy: beats(
        'الاستجابة ثلاثة أشياء: سطر الحالة، والـ headers، والجسم.',
        'قد يكون الجسم مثاليًا بينما تجعله الـ headers غير قابل للاستخدام. وقد تقول الحالة 200 بينما يقول الجسم إن العملية فشلت.',
        'مكتبة العميل تطوي الثلاثة في حكم واحد، وغالبًا ما يكون الخاطئ.',
      ),
      chapter: beats(
        'أربعة مستويات، على خدمة حقيقية في هذه الآلة، تحادثها بيدك قبل أن تغيّر أي شيء.',
        'بيانات صحيحة تُقدَّم بطريقة لا يستطيع أي عميل استخدامها، ومعنى الأرقام، واستدعاء لا يعود أبدًا، وإعادة محاولة كما ينبغي.',
        'لا شيء منها يحتاج إلى الإنترنت. وكلها هي طريقة عمل الإنترنت. ابدأ بسؤال الخادم بنفسك.',
      ),
    },
    fig: {
      you: 'أنت',
      server: 'خادمهم',
      library: 'مكتبة',
      status: 'الحالة',
      headers: 'الـ headers',
      body: 'الجسم',
    },
  },
  database: {
    narration: {
      outlives: beats(
        'تُعاد كتابة الخدمات، وتُستبدل اللغات، ويُعاد بناء الجهاز. أما البيانات فتبقى.',
        'الخطأ في خدمة ظهيرة سيئة. والخطأ الذي يكتب بيانات خاطئة سنة سيئة.',
        'لذا اسأل قاعدة البيانات بنفسك، لا سكربتًا سألها قبل ثلاثة أسابيع.',
      ),
      codd: beats(
        'في عام 1970، انزعج Edgar Codd في IBM من أن كل قاعدة بيانات تجبرك على معرفة مكان البيانات.',
        'فاقترح الجداول، وصفقة جديدة: تطلب ما تريد، ويقرّر النظام كيف يحصل عليه.',
        'تتنازل عن التحكم في الكيفية، وفي المقابل تستطيع أن تسأل أي شيء. وما زالت SQL تبدو هكذا.',
      ),
      slow: beats(
        'لا يكاد شيء في قاعدة البيانات يبطؤ لأن الحاسوب بطيء. بل لأن عدد الصفوف التي تنظر فيها خاطئ.',
        'قرأت كل صف بينما كان يكفيها عشرة، لأن شيئًا لم يخبرها أين تبحث.',
        'أو سألت ألف مرة عمّا كان يمكن سؤاله مرة واحدة، أو انتظرت قفلًا يمسكه غيرها.',
      ),
      plan: beats(
        'ولأن قاعدة البيانات تختار الطريق، يختبئ البطء داخل خيار لم تتخذه أنت.',
        'لذا تتعلّم أن ترى الخطة التي اختارتها، وتطلب منها أن تشرحها.',
        'ثم تغيّر المعلومات التي اختارت بناءً عليها، وغالبًا بإضافة index واحد.',
      ),
      writes: beats(
        'النوع الأخير من الأخطاء يُحدث ضررًا: كتابات حدث بعضها ولم يحدث بعضها.',
        'دفعة أُخذت دون تسجيل طلب. طلب بلا عناصر. مخزون نُقص مرتين.',
        'ولقاعدة البيانات طريقة تجعل ذلك مستحيلًا: إما أن يحدث كله، أو لا يحدث منه شيء.',
      ),
      chapter: beats(
        'أربعة مستويات على قاعدة بيانات حقيقية في هذا الجهاز، فيها بيانات حقيقية.',
        'أجب عن الأسئلة بسؤال البيانات، وأصلح صفحة أبطأت، وجد الحلقة وراء ألف استعلام، واجعل الكتابات الناقصة كاملة.',
        'الأوامر هي sqlite و SQL، وكلاهما الشيء الحقيقي. ابدأ بسؤال البيانات.',
      ),
    },
    fig: {
      service: 'الخدمة',
      data: 'البيانات',
      where: 'أين',
      what: 'ماذا',
      everyRow: 'كل صف',
      allOrNone: 'الكل أو لا شيء',
    },
  },
  api: {
    narration: {
      weather: beats(
        'حتى الآن كنت أنت العميل، وكانت الواجهة البرمجية مثل الطقس.',
        'استدر. شخص آخر هو العميل، وأنت الطقس.',
        'شيفرتك تستطيع إعادة كتابتها بعد الظهر. أما الواجهة المنشورة فلا، لأن شخصًا لم تقابله قط كتب شيفرته عليها.',
      ),
      procedure: beats(
        'لسنوات، تحادثت البرامج عبر HTTP بإرسال استدعاء إجراء إلى عنوان واحد.',
        'نقطة نهاية واحدة، وظرف يسمّي الدالة المطلوب تشغيلها، ثم تُعاد قيمتها.',
        'كان يشبه تمامًا الشيفرة التي تكتبها أصلًا.',
      ),
      fielding: beats(
        'في عام 2000، رأى Roy Fielding أن لـ HTTP مفردات جاهزة، وأن تجاهلها يرمي الويب كله.',
        'العنوان يسمّي شيئًا. والقراءة تفهمها كل ذاكرة مؤقتة على الطريق بأنها آمنة التكرار.',
        'أخفِ كل شيء داخل ظرف واحد، فلا يمكن تخزين شيء مؤقتًا، ولا تخمين شيء.',
      ),
      nouns: beats(
        'لذا فمعظم الواجهات التي تقابلها اليوم أسماء تستطيع مخاطبتها، بمجموعة صغيرة ثابتة من الأفعال.',
        'لا لأن الأسماء أنيقة.',
        'بل لأن كل ما بينك وبين المتصل بك يعرف معناها أصلًا.',
      ),
      wrong: beats(
        'ما يسوء فعلًا: الشكل ينسخ جدولًا، فيصبح كل متصل مربوطًا به.',
        'تغيير «لا يمكن أن يستخدمه أحد» يكسر ثلاثة أشخاص، لأنك لا ترى إلا المتصلين الذين يشتكون.',
        'استجابة كانت جيدة بأربعين صفًا تنتهي مهلتها عند أربعين ألفًا. وفشل لا يستطيع المتصل قراءته لا يترك له إلا مؤشر انتظار.',
      ),
      chapter: beats(
        'أربعة مستويات، على واجهة المتجر نفسها، وتطبيق جوال وشريك يستدعيانها بالفعل.',
        'امنحها شكلًا يمكن تخمينه، وغيّرها دون كسر أحد، وتصفّح البيانات دون تخطّي صفوف، وصِف الأعطال.',
        'قبل أن تنشرها، اسأل: كم سيكلّف تغييرها؟ ابدأ بنقطة النهاية.',
      ),
    },
    fig: {
      client: 'العميل',
      you: 'أنت',
      cache: 'ذاكرة مؤقتة',
    },
  },
  security: {
    narration: {
      adversary: beats(
        'الاستعلام البطيء لا يحاول شيئًا. والقرص الممتلئ لم يكتشف أنك لا تفحص القيمة المُعادة أبدًا.',
        'هذا الفصل مختلف في أمر واحد: هناك شخص على الطرف الآخر، وهو يبحث.',
        'يقرأ تاريخك، لا آخره فقط. ويرسل المُدخل الذي لم تتخيّله، ويجرّب العنوان وفيه معرّف شخص آخر.',
      ),
      edge: beats(
        'لسنوات، عاش الأمن عند حافة الشبكة، وكان التطبيق في الداخل يثق بكل ما يصله.',
        'ثم صار التطبيق هو الحافة. في عام 1998، وصفت مقالة لصق محتوى نموذج ويب داخل جملة SQL: حقن SQL.',
        'ومنذ ذلك الحين يتكرّر الدرس. معظم الاختراقات الحقيقية تدخل من الباب الأمامي، في طلب يبدو سليمًا.',
      ),
      history: beats(
        'في عام 2017، فقدت Equifax سجلات نحو مئة وسبعة وأربعين مليون شخص، عبر ثغرة كان إصلاحها موجودًا منذ شهرين.',
        'وفي عام 2021، حوّلت مكتبة تسجيل لم يخترها معظم المهندسين، بل ورثوها، اسم مستخدم إلى شيفرة تعمل على خوادمهم.',
        'وفي كل عام، أكثر طرق الدخول شيوعًا هي أقلها إثارة: كلمة مرور صالحة تركها أحدهم حيث يمكن العثور عليها.',
      ),
      shapes: beats(
        'تأتي في أربعة أشكال. بيانات اعتماد تنتهي حيث يمكن قراءتها، وحذفها لا يفيد، لأن التاريخ احتفظ بها.',
        'مُدخل يُعامَل كتعليمات: خانة بحث تُلصق داخل شيء يُنفَّذ.',
        'و«مسجّل الدخول» يُحسب «مسموحًا له»: غيّر المعرّف في العنوان، فتحصل على فاتورة شخص آخر. ومكتبة تعتمد عليها فيها ثغرة منشورة.',
      ),
      boring: beats(
        'كل واحد منها مملّ.',
        'ولكل منها إصلاح يستغرق دقائق.',
        'وكل واحد منها كلّف شركة سمعت بها أكثر مما يكلّفها مهندسوها في سنة.',
      ),
      chapter: beats(
        'أربعة مستويات، شكل واحد في كل منها، في المتجر الذي تعرفه.',
        'مفتاح في التاريخ، وخانة بحث تنفّذ أمرًا، ودخول حقيقي يقرأ ما ليس لك، وتحذير أمني عليك التصرّف فيه قبل نهاية اليوم.',
        'لا شيء منها يحتاج إلى براعة. افترض، مرة واحدة، أن أحدًا يبحث، واقرأ عملك كما سيقرؤه.',
      ),
    },
    fig: {
      indifferent: 'غير مبالٍ',
      looking: 'يبحث',
      network: 'الشبكة',
      app: 'التطبيق',
      history: 'التاريخ',
      search: 'بحث',
      loggedIn: 'مسجّل الدخول',
      allowed: 'مسموح له',
      minutes: 'دقائق',
    },
  },
  concurrency: {
    narration: {
      alone: beats(
        'كل دالة كتبتها تفترض أن شيئًا لم يحدث بين سطر والسطر الذي يليه.',
        'لم يكن ذلك صحيحًا قط. شيفرتك تعمل بجانب نسخ منها، على ثماني أنوية، وبين أي سطرين تحدث بقية الآلة كلها.',
        'في معظم الوقت تفلت من العواقب. وهذه هي المشكلة: الخطأ يحدث أحيانًا، تحت الضغط، في يوم التخفيضات.',
      ),
      lunch: beats(
        'لخمس وعشرين سنة، كان علاج الشيفرة البطيئة هو الانتظار، لأن المعالجات كانت تتسارع كل عامين.',
        'ونحو عام 2004 توقف ذلك. صارت المعالجات تحصل على أنوية أكثر بدلًا من ذلك، وانتهى الغداء المجاني.',
        'وأصبح فعل أكثر من شيء في وقت واحد مشكلة كل مبرمج، لا مشكلة المتخصصين وحدهم.',
      ),
      history: beats(
        'كانت النظرية قديمة أصلًا. في عام 1965 سأل Dijkstra: كيف تتفق عدة عمليات على أن تدخل واحدة منها فقط أولًا؟',
        'وجاءت الأعطال مبكرًا. جهاز علاج إشعاعي، Therac-25، كان فيه سباق لا يظهر إلا حين يكتب المشغّل بسرعة.',
        'وفي عام 2003، جعل سباق في برنامج الإنذار أحدًا لا يرى العطل الأول، ففقد خمسة وخمسون مليون شخص الكهرباء.',
      ),
      shapes: beats(
        'أربعة أشكال. قارئان يريان ثلاث قطع متبقية، ويقرّر كلاهما أنها تكفي، ويكتب كلاهما اثنتين. بعت ثماني، ونقص المخزون واحدة.',
        'تتحقق أن ملفًا غير موجود، ثم تنشئه، وقد أنشأه غيرك في ما بين الخطوتين.',
        'مهمتان تمسك كل منهما ما تريده الأخرى، وتنتظران إلى الأبد. أو مهمة نُفّذت، وضاع التأكيد، فتُنفَّذ مرة أخرى.',
      ),
      chance: beats(
        'لا شيء من الإصلاحات صعب. كل منها سطر أو سطران.',
        'الصعب أن العطل مسألة حظ.',
        'الاختبار الأخضر يثبت فقط أن الخطأ لم يحدث هذه المرة، والخطأ الذي لم يحدث هذه المرة هو الخطأ بعينه.',
      ),
      chapter: beats(
        'أربعة مستويات، شكل واحد في كل منها، على متجر يقيم تخفيضات.',
        'بيع القطع الثلاث الأخيرة لأكثر من اللازم، وجعل مهمتين تتناوبان، ونقل العمل إلى طابور، ومهمة ينفّذها الطابور مرتين.',
        'لا شيء منها عن الـ threads. بل عن ملاحظة اللحظة التي افترضت فيها أنك وحدك. ابدأ بشيئين في وقت واحد.',
      ),
    },
    fig: {
      cores: '8 أنوية',
      sold: 'بيع',
      stock: 'المخزون',
      waits: 'ينتظر',
      twice: 'مرتين',
    },
  },
  observability: {
    narration: {
      debugger: beats(
        'كل عادة لديك في تتبّع الأخطاء تكوّنت في مكان تستطيع فيه إيقاف البرنامج وتشغيله من جديد.',
        'الإنتاج ليس كذلك. حدث العطل في الثالثة وأربع عشرة دقيقة فجرًا، لعميل واحد، على واحد من تسعة خوادم، ولا يحدث الآن.',
        'لذا كل ما تعرفه هو ما قرّرت مسبقًا أن تكتبه. وغالبًا ما قُرّر ذلك مصادفةً، قبل أربع سنوات.',
      ),
      kinds: beats(
        'السجلات رائعة لحدث واحد، وعديمة الفائدة لمليون.',
        'والمقاييس أرقام تُجمع لحظة حدوثها، رخيصة بما يكفي للاحتفاظ بها إلى الأبد.',
        'والتتبّعات تلاحق طلبًا واحدًا عبر خدمات كثيرة، وتُظهر أين قضى وقته.',
      ),
      golden: beats(
        'في عام 2016، سمّى كتاب Google عن الموثوقية الأشياء القليلة التي تستحق القياس في كل شيء تقريبًا.',
        'زمن الاستجابة، وحجم الحركة، والأخطاء، والتشبّع: الإشارات الذهبية الأربع.',
        'والخطأ الذي يعيش أطول من كل أداة هو استخدام النوع الخطأ: البحث في السجلات عن اتجاه، أو في لوحة المعلومات عن عميل واحد.',
      ),
      average: beats(
        'المتوسط يخفي العميل الذي غادر. المتوسط مئة وثمانون جزءًا من الثانية، وأبطأ طلب من كل مئة يستغرق أربع ثوانٍ.',
        'والمقياس المقسّم بمعرّف العميل ليس مقياسًا واحدًا بل مليونًا، ويُسقط النظام أثناء الحادثة.',
        'والتتبّع يتوقف عند الحدود، والوقت ضائع في الفجوة التي لا يقيسها أحد.',
      ),
      alerts: beats(
        'ثم التنبيه الذي ينطلق على شيء ليس مشكلة.',
        'المعالج عند تسعين بالمئة، وهذا لا بأس به، فيوقظ أحدهم في الرابعة فجرًا.',
        'كرّر ذلك بما يكفي، ولن يقرأ أحد التنبيهات بعد الآن، وهذا أسوأ من ألا تكون هناك تنبيهات.',
      ),
      chapter: beats(
        'ثلاثة مستويات، على متجر فيه حركة حقيقية.',
        'لوحة معلومات تقول إن الدفع سريع وهو بطيء، وطلب واحد عبر أربع خدمات، وجهاز نداء رنّ مئتي مرة هذا الشهر.',
        'لا شيء منها عن تثبيت أي شيء. بل عن أن تقرّر، قبل الليلة السيئة، ما الذي يجب أن يستطيع النظام إخبارك به.',
      ),
    },
    fig: {
      logs: 'السجلات',
      metrics: 'المقاييس',
      traces: 'التتبّعات',
      latency: 'زمن الاستجابة',
      traffic: 'الحركة',
      errors: 'الأخطاء',
      saturation: 'التشبّع',
      mean: 'المتوسط',
      gap: 'الفجوة',
    },
  },
  scaleCost: {
    narration: {
      bottleneck: beats(
        'في أي لحظة، هناك شيء واحد هو الحدّ: قاعدة بيانات، أو قفل، أو استدعاء بطيء واحد في وسط حلقة.',
        'وكل ما عداه لديه فائض. سرّع أي شيء لديه فائض، ويعمل النظام بالسرعة نفسها تمامًا.',
        'في عام 1967 كتب Gene Amdahl الحساب: اجعل ربع العمل أسرع بلا حدود، فيصبح الكل أسرع بالثلث، لا أكثر أبدًا.',
      ),
      find: beats(
        'لذا فالخطوة الأولى ليست أبدًا جعل شيء أسرع.',
        'بل معرفة ما الذي ينتظر فعلًا.',
        'إن لم تعرف أي جزء هو الحدّ، فاحتمال أن تختاره ضعيف، ويزداد ضعفًا مع كل جزء.',
      ),
      queue: beats(
        'في عام 1961 أثبت John Little أن ما في داخل النظام يساوي سرعة وصول الأشياء، مضروبة في مدة بقاء كل منها.',
        'وقرب السعة الكاملة، لا يرتفع الانتظار بسلاسة. بل يصعد عموديًا.',
        'عند خمسة وتسعين بالمئة، لا تكون الطوابير أطول بأربع مرات مما هي عند سبعين. بل أطول بعشرين مرة.',
      ),
      headroom: beats(
        'ولهذا فعبارة «نحن عند تسعين بالمئة فقط، لدينا متّسع» من أغلى العبارات في هذا المجال.',
        'لثلاثين سنة، كانت السعة أمر شراء وخزانة خوادم، تُدفع مرة واحدة لساعة الذروة.',
        'واليوم تُحسب بالثانية، وتتغيّر حين تغيّر حلقة. ثمن التصميم يمكن قياسه، لذا يمكن أن يكون خاطئًا.',
      ),
      wrong: beats(
        'خزّن مؤقتًا بمفتاح خاطئ، فيُقدَّم لعميل بيانات عميل آخر.',
        'اختبر تحمّل فحص الصحة بعشرة آلاف طلب في الثانية، فيقول التقرير إن كل شيء بخير.',
        'واستعلام يُنفَّذ مرة لكل صف هو صفحة بطيئة على حاسوبك، وفاتورة في الإنتاج.',
      ),
      chapter: beats(
        'ثلاثة مستويات، على متجر بدأ ينشغل.',
        'صفحة بطيئة تصبح سريعة بألا تسأل مرتين، وألف طلب لتجد أين يتوقف الخط عن الاستقامة، وتكلفة ميزة لكل طلب.',
        'قِس قبل أن تغيّر أي شيء، واعرف أن شيئًا واحدًا فقط هو الحدّ، وقل كم يكلّف تصميمك بالمال. ابدأ بألا تسأل مرتين.',
      ),
    },
    fig: {
      limit: 'الحدّ',
      slack: 'فائض',
      capacity: 'السعة',
      waiting: 'الانتظار',
      rack: 'خزانة خوادم',
      perSecond: 'بالثانية',
      yourLaptop: 'حاسوبك',
      invoice: 'فاتورة',
    },
  },
  whyEngineering: {
    narration: {
      job: beats(
        'أن تعرف هل الكود صحيح: هذا كان دائمًا العمل الحقيقي لمهندس البرمجيات.',
        'أن تقرأ كودًا لم تكتبه، وتجد سبب فشل اختبار، وتعرف ما الذي سيكسره أي تغيير.',
        'الذكاء الاصطناعي يجعل كتابة الكود أرخص، ويجعل هذا الحكم أثمن.',
      ),
      workplace: beats(
        'يعلّمك EngineerOS هذا كما يعلّمك العمل نفسه: بأن تمارسه.',
        'تنضمّ إلى Lattice، وهي شركة خيالية، وتعمل في مستودعها الحقيقي، على Linux حقيقي يعمل في تبويب متصفحك.',
        'ويسلّمك زملاؤك بناءً معطّلًا، وعملًا ضائعًا، و pull requests لتراجعها.',
      ),
      teammates: beats(
        'أحد زملائك هو Kite، وكيل برمجة يبدو عمله مكتملًا، حتى حين لا يكون كذلك.',
        'فتتعلّم أن تراجع عمله كما تراجع عمل أي زميل.',
        'أما Iris فهي وكيلتك أنت. أحسِن توجيهها وراجع عملها، فهي مثل أي وكيل تخطئ أحيانًا.',
      ),
      graded: beats(
        'تُقيَّم على الحالة التي تترك عليها المستودع، لا على الأوامر التي تكتبها.',
        'لذا استخدم أي أداة تشاء، بما فيها الذكاء الاصطناعي. المهم أن يكون العمل صحيحًا.',
        'والـ checkpoints تختبر العمل كله بلا تلميحات، وكل نجاح مُتحقَّق منه يظهر في صفحة عامة يمكنك مشاركتها.',
      ),
      path: beats(
        'تبدأ الدورة بأول أمر تكتبه في الـ terminal، وتمضي حتى البناء بنماذج الذكاء الاصطناعي.',
        'فيها أكثر من مئة مستوى، كل منها موقف يواجهه المهندسون في العمل.',
        'المستويات الأولى مجانية، وأولها يستغرق نحو أربع دقائق.',
        'تعلّم هندسة البرمجيات لعصر الذكاء الاصطناعي.',
      ),
    },
    fig: {
      you: 'أنت',
      read: 'تقرأ كود غيرك',
      diagnose: 'تجد سبب الفشل',
      predict: 'تعرف ما سينكسر',
      typing: 'الكتابة',
      judgment: 'الحكم',
      realLinux: 'Linux حقيقي في تبويب متصفحك',
      buildFailing: 'البناء يفشل',
      lostWork: 'ضاع عملي',
      pleaseReview: 'راجعه من فضلك',
      codingAgent: 'وكيل برمجة',
      testsPass: 'الاختبارات تنجح',
      missedCase: 'حالة فاتته',
      yourReview: 'مراجعتك',
      irisBrief: 'وجّهها · راجع عملها',
      whatYouTyped: 'ما كتبته',
      notGraded: 'لا يُقيَّم',
      theState: 'الحالة التي تتركها',
      ai: 'ذكاء اصطناعي',
      anyTool: 'أي أداة',
      checkpoint: 'checkpoint · بلا تلميحات',
      verified: 'مُتحقَّق منه',
      firstCommand: 'أول أمر',
      tests: 'الاختبارات',
      production: 'الإنتاج',
      aiModels: 'نماذج الذكاء الاصطناعي',
      moreThan100: 'أكثر من 100 مستوى',
      free: 'البداية مجانية',
      fourMinutes: 'أول مستوى · نحو 4 دقائق',
      tagline: 'تعلّم هندسة البرمجيات لعصر الذكاء الاصطناعي.',
    },
  },
  swimmerShip: {
    narration: {
      hook: beats(
        'تخيّل كل ما تستطيع البرمجيات فعله محيطًا.',
        'لسبعين عامًا، استكشفه المهندسون بالطريقة الوحيدة المتاحة: بالسباحة. كل سطر من الكود كان ضربة تضربها بنفسك.',
        'والآن لدينا شيء جديد. لدينا سفينة.',
      ),
      swimmer: beats(
        'في أغسطس 1875، أصبح ماثيو ويب، وهو قبطان في البحرية التجارية، أول شخص مسجَّل يعبر القنال الإنجليزي سباحةً.',
        'استغرق ذلك قرابة اثنتين وعشرين ساعة. ظلّ المدّ يدفعه جانبًا، فكان طريقه نحو ضعف المسافة المستقيمة.',
        'هكذا يتعلّم السبّاح الماء: البرد، والتيارات، وإلى أين يريد المدّ أن يأخذك.',
        'والمهندسون الذين كتبوا كل سطر بأيديهم تعلّموا أنظمتهم بالطريقة نفسها. ببطء، وبإتقان.',
      ),
      ship: beats(
        'في ديسمبر 1872، أبحرت HMS Challenger من إنجلترا في رحلة يُقال كثيرًا إنها أول رحلة كبرى لعلم المحيطات.',
        'وعلى مدى ثلاث سنوات ونصف قطعت نحو مئة وثلاثين ألف كيلومتر. وجنوب غرب جزر ماريانا، أنزل طاقمها حبلًا مثقَّلًا لأكثر من ثمانية كيلومترات.',
        'لا يستطيع سبّاح أن يصل إلى هناك أبدًا. لم تجعل السفن الرحلات القديمة أسرع فحسب، بل وصلت إلى أماكن لم يصلها أحد.',
        'وكلاء الذكاء الاصطناعي هم سفينتنا. أنت تختار الوجهة، والوكيل يقرأ الكود ويكتبه ويشغّله، أبعد وأسرع مما يستطيعه أيٌّ منا وحده.',
      ),
      captain: beats(
        'لكن السفينة لا تختار وجهتها. إنها تمضي حيث تُوجَّه، وتمضي بسرعة.',
        'لدى الملّاحين قاعدة تقريبية: انحراف درجة واحدة عن المسار يُبعدك نحو كيلومتر عن هدفك مقابل كل ستين كيلومترًا تقطعها.',
        'السبّاح الذي ينجرف يخطئ ببضعة أمتار. والسفينة التي تنجرف تصل إلى الساحل الخطأ. والوكيل الذي ينجرف قد يكتب ألف سطر خطأ قبل الغداء.',
        'لذا يتحقّق الملّاحون من موقعهم مرة بعد مرة، ويصحّحون. ويراجع المهندسون عمل الوكيل بالطريقة نفسها: يقرؤونه، ويختبرونه، ويثبتون أنه يعمل.',
      ),
      swim: beats(
        'حتى اليوم، على كل مجنّد في البحرية الأمريكية أن يجتاز اختبار سباحة لينهي تدريبه الأساسي.',
        'لا ليعبر المحيطات سباحةً، بل لأنه حين يقع خطأ في عرض البحر، تكون معرفتك بالماء هي ما يعيدك إلى البيت.',
        'الوكلاء يعلقون، ويخطئون أحيانًا بثقة. حينها عليك أن تقرأ الكود بنفسك، وتجد الخطأ، وتصلحه بيدك.',
        'كان ماثيو ويب قبطان سفينة يجيد السباحة. السفينة تأخذك أبعد عن الشاطئ، ولهذا بالذات ما زلت تحتاج إلى السباحة.',
      ),
      close: beats(
        'في يناير 1960، حملت الغوّاصة Trieste شخصين إلى عمق يقارب أحد عشر كيلومترًا، إلى أعمق نقطة في المحيط، مكان يُسمّى اليوم غور تشالنجر.',
        'الآلة هي التي غاصت. أما من كانا بداخلها فقرآ كل مؤشر واتخذا كل قرار.',
        'هذا هو عمل المهندس الآن. تعلّم السباحة. ثم تعلّم الإبحار.',
        'المحيط لم يصغر. لكننا الآن نستطيع أن نصل إلى جزء أكبر منه بكثير.',
      ),
    },
    fig: {
      shore: 'الشاطئ',
      everything: 'كل ما تستطيع البرمجيات فعله',
      eachStroke: 'كل ضربة: سطر واحد من الكود',
      swimRange: 'أبعد ما يبلغه سبّاح واحد',
      aShip: 'سفينة',
      webb: 'الكابتن ماثيو ويب',
      dover: 'دوفر',
      calais: 'كاليه',
      straight: '34 كم في خط مستقيم',
      swimTime: '21 ساعة و45 دقيقة',
      tide: 'المدّ',
      swum: 'نحو 64 كم سباحةً',
      cold: 'البرد',
      currents: 'التيارات',
      tidePull: 'شدّ المدّ',
      howItFails: 'كيف يفشل',
      whereSlow: 'أين يبطؤ',
      whyBuilt: 'لماذا بُني هكذا',
      aTask: 'مهمة',
      done: 'تمّت',
      slowly: 'ببطء، وبإتقان',
      challenger: 'السفينة HMS Challenger',
      voyage: 'ديسمبر 1872 – مايو 1876',
      distance: '≈ 130,000 كم',
      surface: 'السطح',
      metres: 'م',
      diver: 'غوّاص: عشرات الأمتار',
      hairline: 'خيط رفيع بهذا المقياس',
      noOne1: 'أماكن لم',
      noOne2: 'يصلها أحد',
      swimmersRange: 'مدى السبّاح',
      theAgent: 'الوكيل',
      youWhere: 'أنت: إلى أين',
      reads: 'يقرأ',
      writes: 'يكتب',
      runsCode: 'يشغّل الكود',
      fartherFaster: 'أبعد وأسرع',
      navigator: 'ملّاح يرصد الشمس',
      navigatorCredit: 'USS Alaska، 1945، البحرية الأمريكية',
      pointed: 'حيث وُجِّهت',
      oneDegree: 'انحراف 1°',
      km60: '60 كم',
      off1: 'انحراف 1 كم',
      off10: 'انحراف 10 كم بعد 600 كم',
      notToScale: 'الفارق مرسوم أكبر من حجمه',
      aSwimmer: 'سبّاح',
      fewMetres: 'خطأ ببضعة أمتار',
      wrongCoast: 'الساحل الخطأ',
      anAgent: 'وكيل',
      thousandLines: 'ألف سطر خطأ',
      checkCorrect: 'تحقّق من الموقع، وصحّح',
      readIt: 'اقرأه',
      testIt: 'اختبره',
      proveIt: 'أثبت أنه يعمل',
      fixesOnAgent: 'مراجعات المهندس لعمل الوكيل',
      swimTest: 'بحّارة في اختبار سباحة',
      swimTestCredit: 'بيرل هاربر، 2013، البحرية الأمريكية',
      everyRecruit: 'كل مجنّد: يجتاز اختبار سباحة',
      goesWrong: 'يقع خطأ ما',
      knowingWater: 'معرفة الماء',
      stuck: 'عالق، أو مخطئ بثقة',
      youFix: 'أنت: اقرأ، جد الخطأ، أصلحه بيدك',
      webbShort: 'ماثيو ويب',
      captainSwim: 'قبطان يجيد السباحة',
      stillSwim: 'أبعد عن الشاطئ: تعلّم السباحة مع ذلك',
      trieste: 'الـ Trieste، 23 يناير 1960',
      triesteCredit: 'فوق خندق ماريانا، البحرية الأمريكية',
      piccardWalsh: 'جاك بيكار ودون والش',
      insideTrieste: 'داخل الـ Trieste، 1960',
      challengerLine: 'حبل Challenger، 8,184 م',
      deepest: '≈ 10,911 م',
      challengerDeep: 'غور تشالنجر',
      theMachine: 'الآلة',
      didDiving: 'غاصت',
      thePeople: 'البشر',
      readGauges: 'قرؤوا كل مؤشر',
      madeDecisions: 'واتخذوا كل قرار',
      tagline: 'تعلّم السباحة. ثم تعلّم الإبحار.',
    },
  },
};

/** The first two chapters have localized captions and labels. Until local
 * recordings exist they play silently on estimated beats. Later chapters
 * still use the English script and its matching recording. */
export const de: typeof en = {
  ...en,
  ...deContinuation,
  player: {
    title: 'Die Idee als Animation', play: 'Abspielen', pause: 'Pausieren',
    resume: 'Fortsetzen', replay: 'Noch einmal ansehen',
    previous: 'Vorherige Szene', next: 'Nächste Szene',
    mute: 'Erzählstimme ausschalten', unmute: 'Erzählstimme einschalten',
    volume: 'Lautstärke der Erzählstimme',
    rate: (rate: number) => `${num(rate, 'de')}×`,
    rateLabel: (rate: number) => `Abspielgeschwindigkeit ${num(rate, 'de')}×. Ändern`,
    scene: (n: number, of: number) => `Szene ${num(n, 'de')} von ${num(of, 'de')}`,
  },
  firstCommits: {
    narration: {
      copies: beats(
        'Am Anfang steht eine Datei, die funktioniert.',
        'Du willst sie nicht kaputtmachen. Also legst du eine Kopie an. Dann noch eine.',
        'Die nächste heißt „funktioniert wirklich“.',
        'Eine Woche später weiß niemand mehr, welche die richtige ist.',
      ),
      collision: beats(
        'Dann arbeitet ein Kollege mit dir.',
        'Ihr öffnet am selben Nachmittag dieselbe Datei und ändert sie beide.',
        'Wenn ihr einfach eine Fassung über die andere kopiert, bleibt nur eine übrig.',
        'Die Arbeit des anderen geht verloren.',
      ),
      central: beats(
        'Lange lag der Verlauf vieler Teams auf einem zentralen Server.',
        'Die Arbeitskopien enthielten die aktuellen Dateien, aber nicht den ganzen Verlauf.',
        'Fiel die Verbindung aus, war der Verlauf auf dem Server nicht erreichbar.',
      ),
      clones: beats(
        '2005 entstand Git für die Arbeit am Linux-Kernel.',
        'Ein normaler vollständiger Klon enthält die Dateien und ihren erreichbaren Verlauf.',
        'Ein Repository auf GitHub ist eine weitere Kopie, die das Team als gemeinsamen Bezugspunkt wählen kann.',
      ),
      hashes: beats(
        'Ein Commit beschreibt einen Schnappschuss und seine Metadaten, darunter die Eltern. Sein Hash wird aus diesen Daten berechnet.',
        'Änderst du ein Byte in einem alten Commit,',
        'ändern sich sein Hash und die Hashes der darauf aufbauenden Commits. Der umgeschriebene Verlauf hat andere Kennungen.',
      ),
      branches: beats(
        'Ein Branch ist ein Name, der auf einen Commit zeigt.',
        'Ein neuer Branch ist schnell angelegt.',
        'Committest du auf ihm, rückt sein Name vor. Die Projektdateien werden dafür nicht kopiert.',
        'Der Branch-Verweis hält die Kennung eines Commits.',
      ),
      threePlaces: beats(
        'Dieses Kapitel baut auf einer Idee auf: Deine Arbeit kann an drei Stellen liegen.',
        'Zuerst in den Dateien, die du bearbeitest.',
        'Dann in der Staging Area, dem Entwurf deines nächsten Commits.',
        'Nach dem Commit liegt sie im Verlauf.',
        'Wenn Git dich verwirrt, frag dich, welche dieser Stellen gemeint ist. Jetzt kommt dein erster Commit.',
      ),
    },
    fig: {
      whichOne: 'Welche ist richtig?', you: 'du', colleague: 'Kollege',
      lost: 'Arbeit verloren', server: 'Server', history: 'Verlauf',
      filesOnly: 'nur Dateien', offline: 'offline: kein Verlauf',
      yourLaptop: 'dein Rechner', theirLaptop: 'anderer Rechner',
      agreedCopy: 'die gemeinsame Kopie', oneByte: 'ein Byte geändert',
      laterHashes: 'folgende Hashes ändern sich', wholeBranch: 'der ganze Verweis:',
      workingTree: 'Arbeitsverzeichnis', stagingArea: 'Staging Area',
      edited: 'bearbeitet', staged: 'gestagt', committed: 'committet',
    },
  },
  workstation: {
    narration: {
      buttons: beats(
        'Programme haben Schaltflächen.',
        'Eine Schaltfläche kann nur ausführen, wofür sie gebaut wurde.',
        'Willst du vierhundert Dateien umbenennen, brauchst du ohne passende Funktion vierhundert Klicks.',
      ),
      teletype: beats(
        'Die Kommandozeile ist älter als der Bildschirm. 1969 war ein Terminal bei Bell Labs ein Fernschreiber am Computer.',
        'Er druckte jeden Buchstaben auf Papier, etwa zehn pro Sekunde.',
        'Deshalb bekamen Befehle kurze Namen wie ls und cp.',
      ),
      pipe: beats(
        'Mit der Pipe wird die Ausgabe eines Programms zur Eingabe des nächsten.',
        'Durchsuche Dateien, behalte die Zeilen mit einem Preis, sortiere sie und zähle sie.',
        'Diese Funktion setzt du aus vier kleinen Werkzeugen zusammen.',
      ),
      survived: beats(
        'Mit grafischen Oberflächen verschwand die Texteingabe aus vielen alltäglichen Aufgaben.',
        'Wer Computer betreibt, nutzt sie weiterhin.',
        'Ein Befehl ist eine genaue Anweisung, die das Programm ausführt.',
      ),
      servers: beats(
        'Er lässt sich wiederholen: In einer Datei gespeichert, kann ihn ein Rechner auch nachts ohne dich ausführen.',
        'Server, Container und CI-Runner haben oft keine grafische Oberfläche.',
        'Auch dort arbeitest du mit Textbefehlen wie denen in diesem Terminal.',
      ),
      real: beats(
        'Hier läuft ein echtes Linux auf einem emulierten Rechner in deinem Browser.',
        'Auch ein Befehl, den wir nicht vorgesehen haben, liefert ein echtes Ergebnis oder eine echte Fehlermeldung.',
        'Geprüft wird der Zustand des Rechners, nicht welche Befehle du eingegeben hast.',
        'Du kannst deinen eigenen Weg wählen. Gib jetzt deinen ersten Befehl ein.',
      ),
    },
    fig: {
      open: 'Öffnen', save: 'Speichern', print: 'Drucken',
      renameAll: '400 umbenennen', byHand: 'Klick für Klick',
      teletype: 'Fernschreiber, 1969', computer: 'Computer',
      tenASecond: '~10 Zeichen pro Sekunde', fourTools: 'vier kleine Werkzeuge',
      desktop: 'die grafische Oberfläche', professionals: 'im Beruf geblieben',
      isTheAction: 'die genaue Anweisung', everyNight: 'jede Nacht, ohne dich',
      noScreen: 'ohne Bildschirm', realLinux: 'echtes Linux im Browser',
      keystrokes: 'deine Eingaben', state: 'Zustand des Rechners',
    },
  },
};

export const ja: typeof en = {
  ...en,
  ...jaContinuation,
  player: {
    title: 'アニメーションで理解する', play: '再生', pause: '一時停止',
    resume: '再開', replay: 'もう一度見る', previous: '前のシーン', next: '次のシーン',
    mute: 'ナレーションをオフにする', unmute: 'ナレーションをオンにする',
    volume: 'ナレーションの音量', rate: (rate: number) => `${num(rate, 'ja')}×`,
    rateLabel: (rate: number) => `再生速度${num(rate, 'ja')}×。変更する`,
    scene: (n: number, of: number) => `シーン${num(n, 'ja')}/${num(of, 'ja')}`,
  },
  firstCommits: {
    narration: {
      copies: beats(
        '最初は、動くファイルが1つあります。',
        '壊すのが心配なので、コピーを作ります。さらにもう1つ作ります。',
        '次のコピーには「本当に動く」と名前を付けます。',
        '1週間後には、どれが正しい版なのかわからなくなります。',
      ),
      collision: beats(
        'そこへ同僚が加わります。',
        '同じ午後に、2人とも同じファイルを開いて編集します。',
        '片方の版で上書きすると、残るのは1つだけです。',
        'もう1人の作業が失われます。',
      ),
      central: beats(
        '長い間、多くのチームは中央のサーバーに履歴を保存していました。',
        '作業用のコピーには最新のファイルがあり、完全な履歴はありませんでした。',
        '接続が切れると、サーバー上の履歴を参照できません。',
      ),
      clones: beats(
        '2005年、Linuxカーネルの開発のためにGitが生まれました。',
        '通常の完全なクローンには、ファイルと到達できる履歴が含まれます。',
        'GitHub上のリポジトリも別のコピーで、チームの共有先として選べます。',
      ),
      hashes: beats(
        'コミットはスナップショットと、親などの情報を記録します。ハッシュはそのデータから計算されます。',
        '古いコミットのデータを1バイト変えると、',
        'そのハッシュと、それにつながるコミットのハッシュも変わります。書き換えた履歴には別の識別子が付きます。',
      ),
      branches: beats(
        'ブランチは、1つのコミットを指す名前です。',
        '新しいブランチはすぐに作れます。',
        'そこでコミットすると名前が進みます。ファイル全体をコピーするわけではありません。',
        'ブランチの参照が保持するのは、コミットの識別子です。',
      ),
      threePlaces: beats(
        'この章の土台になる考え方です。作業には3つの置き場所があります。',
        '最初は、編集しているファイルです。',
        '次はステージング領域で、次のコミットの下書きをまとめます。',
        'コミットすると履歴に保存されます。',
        'Gitの表示に迷ったら、3つのどれを示しているか確認してください。最初のコミットを作りましょう。',
      ),
    },
    fig: {
      whichOne: 'どれが正しい版？', you: '自分', colleague: '同僚', lost: '失われた作業',
      server: 'サーバー', history: '履歴', filesOnly: 'ファイルだけ',
      offline: 'オフラインでは履歴なし', yourLaptop: '自分のPC', theirLaptop: '同僚のPC',
      agreedCopy: '共有先のコピー', oneByte: '1バイト変更', laterHashes: '後続のハッシュも変更',
      wholeBranch: 'ブランチの参照:', workingTree: '作業ツリー', stagingArea: 'ステージング領域',
      edited: '編集済み', staged: 'ステージ済み', committed: 'コミット済み',
    },
  },
  workstation: {
    narration: {
      buttons: beats(
        'プログラムにはボタンがあります。',
        'ボタンができるのは、用意された操作だけです。',
        '400個のファイル名を変えたくても、まとめて変える機能がなければ400回クリックします。',
      ),
      teletype: beats(
        'コマンドラインは画面より古い仕組みです。1969年のBell Labsでは、コンピュータにつないだテレタイプがターミナルでした。',
        '1秒に約10文字ずつ、紙に打ち出していました。',
        'そのため、lsやcpのように短いコマンド名が使われました。',
      ),
      pipe: beats(
        'パイプは、1つのプログラムの出力を次のプログラムの入力にします。',
        'ファイルを検索し、価格のある行を残し、並べ替え、数えます。',
        '4つの小さな道具を組み合わせて、必要な操作を作れます。',
      ),
      survived: beats(
        'グラフィカルな画面が広まると、多くの操作で文字入力は見えなくなりました。',
        'コンピュータを運用する人は、今も使っています。',
        'コマンドは、プログラムに実行させる正確な指示だからです。',
      ),
      servers: beats(
        'ファイルに保存すれば繰り返せます。自分がいない夜中でも、コンピュータが実行できます。',
        'サーバー、コンテナ、CIランナーには、グラフィカルな画面がないこともよくあります。',
        'そこでも、このターミナルと同じように、文字のコマンドで作業します。',
      ),
      real: beats(
        'このコースでは、ブラウザ内のエミュレートしたコンピュータで本物のLinuxが動いています。',
        '私たちが想定していないコマンドでも、実際の結果やエラーが返ります。',
        '採点するのは入力した手順ではなく、コンピュータの状態です。',
        '自分の方法で進められます。最初のコマンドを入力しましょう。',
      ),
    },
    fig: {
      open: '開く', save: '保存', print: '印刷', renameAll: '400個の名前を変更',
      byHand: '1回ずつクリック', teletype: 'テレタイプ、1969年', computer: 'コンピュータ',
      tenASecond: '1秒に約10文字', fourTools: '4つの小さな道具', desktop: 'グラフィカルな画面',
      professionals: '仕事では今も使う', isTheAction: '正確な指示', everyNight: '夜も自動で実行',
      noScreen: '画面なし', realLinux: 'ブラウザで本物のLinux', keystrokes: '入力した手順', state: 'コンピュータの状態',
    },
  },
};
